import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { flushSync } from 'react-dom';
import { Spinner, Modal, Button } from 'react-bootstrap';
import { useAuth, db } from "../../../base";
import { getDoc, updateDoc, setDoc, doc, deleteField, runTransaction } from "firebase/firestore";
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from "react-i18next";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { waitForAnimationFrames } from "../../../helpers/helpers";
import { IMAGE_RESOLUTIONS, getGenerationCreditCost, getGenerationCreditUsage } from "../../../helpers/generationPricing";
import OnboardingTooltip from "../../OnboardingTooltip";
import planLimits from "../../../plans/plansLimits";
import './FacadeDesigner.css';
import { colorList } from '../../colors/colorHelper'; // Import the helper file
import { normalizeToCamelCase, normalizeProposedColors } from '../../colors/colorUtils';
import DetectionButton from '../DetectionButton';

// Helper to filter and sort detections (from aiDetections.js logic)
const filterAndSortDetections = (detections) => {
  if (!detections || detections.length === 0) return [];
  
  // Calculate IoU (Intersection over Union)
  const calculateIoU = (boxA, boxB) => {
    const [topA, leftA, bottomA, rightA] = boxA;
    const [topB, leftB, bottomB, rightB] = boxB;
    const intersectTop = Math.max(topA, topB);
    const intersectLeft = Math.max(leftA, leftB);
    const intersectBottom = Math.min(bottomA, bottomB);
    const intersectRight = Math.min(rightA, rightB);
    const intersectWidth = Math.max(0, intersectRight - intersectLeft);
    const intersectHeight = Math.max(0, intersectBottom - intersectTop);
    const intersectArea = intersectWidth * intersectHeight;
    const areaA = (bottomA - topA) * (rightA - leftA);
    const areaB = (bottomB - topB) * (rightB - leftB);
    const unionArea = areaA + areaB - intersectArea;
    return unionArea > 0 ? intersectArea / unionArea : 0;
  };

  // Filter out overlapping windows/doors
  const filteredDetections = [];
  const processedIndexes = new Set();
  for (let i = 0; i < detections.length; i++) {
    if (processedIndexes.has(i)) continue;
    const detectionA = detections[i];
    for (let j = i + 1; j < detections.length; j++) {
      if (processedIndexes.has(j)) continue;
      const detectionB = detections[j];
      const iou = calculateIoU(detectionA.tlbr, detectionB.tlbr);
      if (iou > 0.5) {
        if (detectionA.class === "door" && detectionB.class === "window") {
          processedIndexes.add(j);
        } else if (detectionA.class === "window" && detectionB.class === "door") {
          processedIndexes.add(i);
        }
      }
    }
    if (!processedIndexes.has(i)) {
      filteredDetections.push(detectionA);
    }
  }

  // Sort left-to-right by left edge coordinate
  return filteredDetections.sort((a, b) => {
    const leftA = a.tlbr[1];
    const leftB = b.tlbr[1];
    return leftA - leftB;
  });
};

const REAL_MODE_EXAMPLE_IMAGES = [
  {
    src: '/maisons-modeles/converted_images/before-ai-editor-design.webp',
    altKey: 'homeAiGeneration.beforeAlt',
    altDefault: 'Initial design image created in the editor',
    labelKey: 'homeAiGeneration.beforeLabel',
    labelDefault: 'BEFORE · Editor Design'
  },
  {
    src: '/maisons-modeles/converted_images/after-ai-generation.webp',
    altKey: 'homeAiGeneration.finalAlt',
    altDefault: 'Accepted final project image generated with AI',
    labelKey: 'homeAiGeneration.finalLabel',
    labelDefault: 'FINAL · AI Finalized'
  }
];

const MAX_REFERENCE_IMAGES = 8;

const FacadeDesigner = ({ projectChangeKey, projects, projectId, hasCanvasChanged = false, generatedProjectImages = [], applyColorsToCanvasItems, handleShowLoader, handleHideLoader, newProjectImage, triggerAIDetectionForOpenedProject, detectAndApplyColors, setIsDetectionTriggered, fabricCanvas, isDetectionTriggered, resetNewProjectImage, userRole, isDesignerModalOpen, setDesignerModalOpen, handleAnalyticsEvent, setRoboflowFoundDetections, setGeminiScaleFactor, backgroundImage, backgroundImageUrl: selectedBaseImageUrl, setBackgroundImageUrlInApp, onConversationSaveReady, onReferenceImageActionReady, setProjectScaleFactor, onFacadeAIGeneratedImage, saveUpdate, isOriginalProjectBaseActive = true, originalProjectImageUrl = '', onFacadeAIGenerationStateChange }) => {
  const { t, i18n } = useTranslation(["translation"]);
  const { isStepActive, completeStep, updateAppState, isOnboardingActive, currentStep } = useOnboarding();
  const currentUser = useAuth();
  const textAreaRef = useRef(null);
  const textareaContainerRef = useRef(null); // Add a ref for the textarea container
  const responseContainerRef = useRef(null);
  const modalBodyRef = useRef(null);
  const generationStatusRef = useRef(null);
  const referenceFileInputRef = useRef(null);
  const prevBodyOverflowRef = useRef(null);
  const lastProcessedProjectIdRef = useRef(null); // Track last processed projectId to prevent unnecessary clears
  const lastProjectChangeKeyRef = useRef(null); // Track project changes to detect new projects
  const hasOpenedDesignerDuringOnboardingRef = useRef(false);
  const [designerTriggerContainer, setDesignerTriggerContainer] = useState(null);
  const [designerModalLayout, setDesignerModalLayout] = useState({});

  const [userInput, setUserInput] = useState('');
  const [assistantResponse, setAssistantResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowMode, setWorkflowMode] = useState('ideas');
  const [workflowProgress, setWorkflowProgress] = useState('');
  const [showMeModalContainer, setShowMeModalContainer] = useState(null);
  const [userData, setUserData] = useState({});
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [defaultQuestion, setDefaultQuestion] = useState('');
  const [isFirstInteraction, setIsFirstInteraction] = useState(true);
  const [threadId, setThreadId] = useState(null);
  const [runId, setRunId] = useState(null);
  const [imageFileId, setImageFileId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [designerCollapsed, setDesignerCollapsed] = useState(() => {
    try {
      return localStorage.getItem('floatingDesignerCollapsed') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [colorButtons, setColorButtons] = useState([]);
  const [proposedColors, setProposedColors] = useState({});
  const [conversationHistory, setConversationHistory] = useState([]); // Add this line
  const proposedColorsRef = useRef(proposedColors);
  const lastSentBackgroundImageRef = useRef(null);
  const autoSaveConversationTimerRef = useRef(null);
  const lastAutoSavedConversationSignatureRef = useRef('');
  const [isProjectSwitching, setIsProjectSwitching] = useState(false); // Track project switching state
  const [isSaving, setIsSaving] = useState(false); // Track saving state
  const [currentLanguage, setCurrentLanguage] = useState("en"); // Default to English
  const [errorMessage, setErrorMessage] = useState(""); // Error message state
  const [isFacadeAIGenerating, setIsFacadeAIGenerating] = useState(false);
  const [imageResolution, setImageResolution] = useState('1K');
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const resolutionChoiceRef = useRef(null);
  useEffect(() => () => {
    if (resolutionChoiceRef.current) resolutionChoiceRef.current(null);
  }, []);
  const [facadeAIGenerationStartedAt, setFacadeAIGenerationStartedAt] = useState(null);
  const [facadeAIGenerationElapsedSeconds, setFacadeAIGenerationElapsedSeconds] = useState(0);
  const [referenceImages, setReferenceImages] = useState([]);
  const [referencePreviewImage, setReferencePreviewImage] = useState(null);
  const [showMakeItRealNoDesignModal, setShowMakeItRealNoDesignModal] = useState(false);
  const [showMakeItRealNoDesignVideo, setShowMakeItRealNoDesignVideo] = useState(false);
  const [expandedHistoryIndex, setExpandedHistoryIndex] = useState(null);
  const [activeDesignerMode, setActiveDesignerMode] = useState('ideas');
  const suppressOnboardingCloseSkipRef = useRef(false);
  const isGuestUser = Boolean(currentUser?.email?.startsWith('guest_'));

  useEffect(() => {
    setShowMeModalContainer(document.body);
  }, []);
  const isAdminGuestTestingSession = (() => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return sessionStorage.getItem('adminGuestMode') === 'true';
    } catch (storageError) {
      return false;
    }
  })();

  // Initialize language on component mount
  useEffect(() => {
    const language = initializeLanguage();
    setCurrentLanguage(language);
  }, []);

  useEffect(() => {
    if (!isFacadeAIGenerating || !facadeAIGenerationStartedAt) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setFacadeAIGenerationElapsedSeconds(
        Math.max(0, Math.floor((Date.now() - facadeAIGenerationStartedAt) / 1000))
      );
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isFacadeAIGenerating, facadeAIGenerationStartedAt]);

  useEffect(() => {
    if (!isFacadeAIGenerating) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (generationStatusRef.current && typeof generationStatusRef.current.scrollIntoView === 'function') {
        generationStatusRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        return;
      }

      if (modalBodyRef.current && typeof modalBodyRef.current.scrollTo === 'function') {
        modalBodyRef.current.scrollTo({
          top: modalBodyRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [isFacadeAIGenerating]);

  useEffect(() => {
    if (typeof onFacadeAIGenerationStateChange !== 'function') {
      return undefined;
    }

    onFacadeAIGenerationStateChange({
      isGenerating: isFacadeAIGenerating,
      elapsedSeconds: facadeAIGenerationElapsedSeconds,
    });

    return () => {
      onFacadeAIGenerationStateChange({
        isGenerating: false,
        elapsedSeconds: 0,
      });
    };
  }, [facadeAIGenerationElapsedSeconds, isFacadeAIGenerating, onFacadeAIGenerationStateChange]);

  // Debug function to log conversation data from Firebase
  const logFirebaseConversationData = async () => {
    if (!projectId) {
      console.log("❌ No projectId available for logging conversation data");
      return;
    }

    try {
      console.log("🔍 Querying Firebase for conversation data...");
      console.log("📁 Project ID:", projectId);

      // Get the project document
      const projectRef = doc(db, "Projects", projectId);
      const projectSnap = await getDoc(projectRef);
      
      if (!projectSnap.exists()) {
        return;
      }

      const projectData = projectSnap.data();

      // Check for conversation data
      const conversationData = projectData.conversationData;
      if (conversationData && conversationData.conversationHistory && conversationData.conversationHistory.length > 0) {
        // Conversation data exists and can be processed
      }

    } catch (error) {
      console.error("❌ Error logging Firebase conversation data:", error);
    }
  };

  useEffect(() => {
    proposedColorsRef.current = proposedColors;
  }, [proposedColors]);

  // Monitor modal state during onboarding
  useEffect(() => {
    // If modal is closed during AI steps, complete the flow and go to save-limitations
    if (
      hasOpenedDesignerDuringOnboardingRef.current &&
      !suppressOnboardingCloseSkipRef.current &&
      !isDesignerModalOpen &&
      (isStepActive('ai-interaction') || isStepActive('submit-request') || isStepActive('processing-request') || isStepActive('recommendations-ready'))
    ) {
      console.log('🎯 OnboardingDebug: Designer modal closed during AI steps, completing onboarding flow');
      // Complete current step and move to save-limitations
      completeStep(currentStep);
      // Use a small delay to ensure state updates
      setTimeout(() => {
        completeStep('submit-request'); // Complete this step if not already
        completeStep('processing-request'); // Complete this step if not already
        completeStep('recommendations-ready'); // Complete this step if not already
        completeStep('editing-tools'); // Complete this step to reach save-limitations
      }, 100);
    }
  }, [isDesignerModalOpen, isStepActive, currentStep, completeStep]);

  // Monitor loading state during onboarding - ensure step progression when AI response is received
  useEffect(() => {
    if (!isLoading && isStepActive('processing-request')) {
      // If loading finished but we're still on processing-request step, force progression
      console.log('🎯 OnboardingDebug: Loading finished, forcing progression from processing-request');
      updateAppState({ aiLoading: false });
      completeStep('processing-request');
    }
  }, [isLoading, isStepActive, completeStep, updateAppState]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // useEffect(() => {
  //   if (textareaContainerRef.current) {
  //     // Scroll the textarea into view after the assistant response is updated
  //     textareaContainerRef.current.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, [assistantResponse]); // Update positioning after each assistant response

  useEffect(() => {
    // Scroll to textareaContainerRef when assistant response is updated or loading finishes
    if (textareaContainerRef.current && !isLoading) {
      textareaContainerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [assistantResponse, isLoading]);

  // Start onboarding when component loads for guest users
  useEffect(() => {
    const isGuestUser = currentUser && currentUser.email.startsWith("guest_");
    if (isGuestUser && projectId) {
      // Update app state to show editor is ready
      updateAppState({ 
        editorVisible: true, 
        projectName: projectId,
        aiDesignerVisible: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, projectId]); // Only depend on currentUser and projectId to prevent infinite loop

  useEffect(() => {
    hasOpenedDesignerDuringOnboardingRef.current = false;
  }, [projectId]);

  useEffect(() => {
    setExpandedHistoryIndex(null);
  }, [conversationHistory.length, projectId]);

  useEffect(() => {
    if (isDesignerModalOpen) {
      suppressOnboardingCloseSkipRef.current = false;
      setActiveDesignerMode('ideas');
    }
  }, [isDesignerModalOpen]);

  useEffect(() => {
    updateAppState({
      aiShowModeSelected: isDesignerModalOpen && activeDesignerMode === 'show'
    });
  }, [activeDesignerMode, isDesignerModalOpen, updateAppState]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    let mounted = true;

    const resolveTriggerContainer = () => {
      if (!mounted) return;
      const nextContainer = document.querySelector('.generatedDesignerTriggerSlot');
      setDesignerTriggerContainer(nextContainer || null);
    };

    resolveTriggerContainer();

    const timer = window.setTimeout(resolveTriggerContainer, 250);

    return () => {
      mounted = false;
      window.clearTimeout(timer);
    };
  }, [projectId, isDesignerModalOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const updateDesignerModalLayout = () => {
      if (window.innerWidth <= 600) {
        setDesignerModalLayout({});
        return;
      }

      const canvasMainArea = document.querySelector('.canvasMainArea');
      const canvasContainer = document.querySelector('.canvasContainer, .canvas-container');
      const rail = document.querySelector('.generatedImagesRail');

      if (!canvasMainArea || !canvasContainer || !rail) {
        setDesignerModalLayout({});
        return;
      }

      const canvasMainAreaRect = canvasMainArea.getBoundingClientRect();
      const canvasContainerRect = canvasContainer.getBoundingClientRect();
      const railRect = rail.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const topPadding = 16;
      const bottomPadding = 16;
      const desiredWidth = Math.min(1120, Math.max(760, viewportWidth - canvasMainAreaRect.left - 40));
      const left = Math.max(
        16,
        Math.min(viewportWidth - desiredWidth - 16, railRect.right + 12)
      );
      let top = Math.max(topPadding, canvasContainerRect.top);
      let bottom = bottomPadding;
      let availableHeight = viewportHeight - top - bottom;

      if (availableHeight < 420) {
        top = Math.max(topPadding, viewportHeight - bottomPadding - 420);
        availableHeight = viewportHeight - top - bottom;
      }

      setDesignerModalLayout({
        '--designer-modal-top': `${Math.round(top)}px`,
        '--designer-modal-bottom': `${Math.round(bottom)}px`,
        '--designer-modal-left': `${Math.round(left)}px`,
        '--designer-modal-width': `${Math.round(desiredWidth)}px`,
        '--designer-modal-height': `${Math.round(Math.max(availableHeight, 320))}px`
      });
    };

    if (isDesignerModalOpen) {
      updateDesignerModalLayout();
    }

    window.addEventListener('resize', updateDesignerModalLayout);
    window.addEventListener('scroll', updateDesignerModalLayout, true);

    return () => {
      window.removeEventListener('resize', updateDesignerModalLayout);
      window.removeEventListener('scroll', updateDesignerModalLayout, true);
    };
  }, [isDesignerModalOpen, projectId]);

  // ✅ Expose conversation save functionality to parent component
  useEffect(() => {
    if (onConversationSaveReady && projectId) {
      const saveConversationData = async () => {
        if (isSaving) {
          return;
        }
        
        // 🚫 CRITICAL: Don't save if we're in the middle of switching projects or have no valid data
        if (isProjectSwitching) {
          return;
        }
        
        if (!threadId || !conversationHistory || conversationHistory.length === 0) {
          return;
        }
        
        setIsSaving(true);
        try {
          
          // 🧹 ALWAYS run cleanup before any save operation
          const cleanupPerformed = await cleanupContaminatedConversationData();
          if (cleanupPerformed) {
            console.log("🔄 Cleanup completed during save, reloading project data...");
            // Reload current state after cleanup
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          
          if (threadId && conversationHistory.length > 0) {
            await saveProjectConversationData(threadId, runId, conversationHistory, imageFileId);
          } else {
            console.log("ℹ️ No conversation data to save - threadId:", threadId, "conversationLength:", conversationHistory.length);
          }
        } finally {
          setIsSaving(false);
        }
      };
      
      // Pass the save function to parent
      onConversationSaveReady(saveConversationData);
    }
  }, [onConversationSaveReady, projectId, threadId, runId, conversationHistory?.length, imageFileId]);

  // ✅ Auto-save conversation updates (without requiring manual project save)
  useEffect(() => {
    return () => {
      if (autoSaveConversationTimerRef.current) {
        clearTimeout(autoSaveConversationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const isGuestUser = currentUser && currentUser.email.startsWith("guest_");
    if (isGuestUser || !projectId || isProjectSwitching || !conversationHistory || conversationHistory.length === 0) {
      return;
    }

    const latestEntry = conversationHistory[0] || {};
    const conversationSignature = [
      projectId,
      threadId || '',
      runId || '',
      imageFileId || '',
      conversationHistory.length,
      latestEntry.question || '',
      latestEntry.response || ''
    ].join('||');

    if (conversationSignature === lastAutoSavedConversationSignatureRef.current) {
      return;
    }

    if (autoSaveConversationTimerRef.current) {
      clearTimeout(autoSaveConversationTimerRef.current);
    }

    autoSaveConversationTimerRef.current = setTimeout(async () => {
      try {
        await saveProjectConversationData(threadId, runId, conversationHistory, imageFileId);
        lastAutoSavedConversationSignatureRef.current = conversationSignature;
      } catch (autoSaveError) {
        console.error('❌ Auto-save conversation failed:', autoSaveError);
      }
    }, 900);

    return () => {
      if (autoSaveConversationTimerRef.current) {
        clearTimeout(autoSaveConversationTimerRef.current);
      }
    };
  }, [projectId, threadId, runId, imageFileId, conversationHistory, isProjectSwitching, currentUser]);

  // Handle onboarding transition to editor-loaded step
  useEffect(() => {
    const isGuestUser = currentUser && currentUser.email.startsWith("guest_");
    
    // We don't need to complete project-selection here since Account component does it
    // Just log for debugging
    if (isGuestUser && projectId) {
      // Onboarding check for guest user with project
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, projectId]); // Only depend on currentUser and projectId

  useEffect(() => {
    const normalizeProposedColors = (colors, lang) => {
      const normalizedColors = {};
      Object.keys(colors).forEach(key => {
        const normalizedKey = normalizeItemKey(key);
        const translatedKey = itemMapping[lang][normalizedKey] || normalizedKey;
        normalizedColors[translatedKey] = colors[key];
      });
      return normalizedColors;
    };

    const normalizedColors = normalizeProposedColors(proposedColors, currentLanguage);
    setProposedColors(normalizedColors);
  }, [currentLanguage]); // Trigger when language changes


  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        if (currentUser.email.includes('guest')) {
          // Retrieve interaction counts from local storage for guest users
          const weeklyInteractionCount = isAdminGuestTestingSession
            ? 0
            : (parseInt(localStorage.getItem('guest_weeklyInteractionCount')) || 0);
          const assistantInteractionCount = isAdminGuestTestingSession
            ? 0
            : (parseInt(localStorage.getItem('guest_assistantInteractionCount')) || 0);

          // Set guest users to the Guest plan, unless this is an admin guest testing session.
          setUserData({
            weeklyInteractionCount,
            assistantInteractionCount,
            planType: isAdminGuestTestingSession ? 'Admin' : 'Guest',
          });
        } else {
          // Retrieve data from Firestore for regular users
          const userRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        }
      }
    };

    fetchUserData();
  }, [currentUser, isAdminGuestTestingSession]);

  // Load existing conversation data when component mounts
  useEffect(() => {
    const initializeProjectData = async () => {
      // 🆕 DETECT NEW PROJECT: Check if projectChangeKey has changed (indicates new project created)
      const isNewProject = lastProjectChangeKeyRef.current !== null && lastProjectChangeKeyRef.current !== projectChangeKey;
      
      if (isNewProject) {
        // Clear everything for new project
        clearConversationState();
        lastProcessedProjectIdRef.current = projectId;
        lastProjectChangeKeyRef.current = projectChangeKey;
        
        // Initialize the new project with image upload and fileId
        await initializeNewProject(projectId);
        return; // Don't try to load conversation data for new projects
      }
      
      // Update project change key tracking
      if (lastProjectChangeKeyRef.current === null) {
        lastProjectChangeKeyRef.current = projectChangeKey;
      }
      
      // 🧹 ONLY clear state when projectId actually changes (not on re-renders) for existing projects
      if (projectId && lastProcessedProjectIdRef.current !== projectId) {
        // Use the dedicated clear function to ensure complete cleanup (but preserve user input)
        clearConversationState();
        
        lastProcessedProjectIdRef.current = projectId;
      }
      
      if (!projectId) {
        setConversationHistory([]);
        setThreadId(null);
        setRunId(null);
        lastProcessedProjectIdRef.current = null;
      } else if (isProjectSwitching) {
        // Project switching in progress - wait for completion
      }
    };

    initializeProjectData();
  }, [projectId, projectChangeKey]); // Include projectChangeKey to detect new projects

  // Separate useEffect to handle data loading when switching completes
  useEffect(() => {
    let cancelled = false;
    const safeSet = (setter, value) => {
      if (!cancelled && isMountedRef.current) {
        setter(value);
      }
    };

    const loadProjectData = async () => {
      if (projectId && !isProjectSwitching) {
        // 🧹 Step 1: Perform cleanup FIRST, before any data loading
        const cleanupPerformed = await cleanupContaminatedConversationData();
        
        if (cleanupPerformed) {
          // Give time for Firebase to sync
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        // 🔄 Step 2: Load conversation data
        try {
          const projectRef = doc(db, "Projects", projectId); // ✅ Fixed: Use "Projects" instead of "projects"
          const projectDoc = await getDoc(projectRef);
          
          if (projectDoc.exists()) {
            const projectData = projectDoc.data();
              
            // ✅ Priority order: 1) Main project threadId, 2) Conversation threadId
            // This ensures conversations stay connected to the AI evaluation thread
            let primaryThreadId = null;
            
            if (projectData.threadId) {
              primaryThreadId = projectData.threadId;
            } else if (projectData.conversationData && projectData.conversationData.threadId) {
              primaryThreadId = projectData.conversationData.threadId;
            }
            
            if (primaryThreadId) {
              safeSet(setThreadId, primaryThreadId);
            } else {
              safeSet(setThreadId, null);
            }
            
            // ✅ Handle conversation data loading with thread isolation
            if (projectData.conversationData) {
              const conversationData = projectData.conversationData;
              
              // ✅ CRITICAL: Only use conversation data if it belongs to this specific project
              // Check if this conversation was created for this project specifically
              const conversationProjectId = conversationData.projectId || projectData.conversationProjectId;
              
              if (conversationProjectId && conversationProjectId !== projectId) {
                safeSet(setConversationHistory, []);
                safeSet(setThreadId, null);
                safeSet(setRunId, null);
                
                // Clean the contaminated data from Firebase
                await cleanContaminatedConversationData(projectId);
              } else {
                if (conversationData.runId) {
                  safeSet(setRunId, conversationData.runId);
                } else {
                  safeSet(setRunId, null);
                }
                
                if (conversationData.conversationHistory && Array.isArray(conversationData.conversationHistory)) {
                  safeSet(setConversationHistory, conversationData.conversationHistory);
                } else {
                  safeSet(setConversationHistory, []);
                }
              }
            }
            // ✅ Handle OLD flat format (runId and conversation history at root level) 
            // Note: threadId is already set above in primary threadId logic
            else if (projectData.threadId || projectData.fileId) {
              console.log("Loading conversation data from OLD flat format for project", projectId);
              
              // ✅ CRITICAL: For old format, also check for contamination
              // If we detect thread contamination, clear everything and start fresh
              const storedThreadId = projectData.threadId;
              
              if (storedThreadId && threadId && storedThreadId !== threadId) {
                console.warn(`🚫 Old format contamination detected! Stored threadId ${storedThreadId} doesn't match current ${threadId}. Starting fresh.`);
                safeSet(setConversationHistory, []);
                safeSet(setThreadId, null);
                safeSet(setRunId, null);
                
                // Clean the contaminated data from Firebase
                await cleanContaminatedConversationData(projectId);
              } else {
                console.log("ℹ️ ThreadId already set from primary logic, not overriding");
                
                if (projectData.runId) {
                  safeSet(setRunId, projectData.runId);
                } else {
                  safeSet(setRunId, null);
                }
                
                if (projectData.conversationHistory && Array.isArray(projectData.conversationHistory)) {
                  console.log("Loading existing conversation history (flat) for project", projectId, ":", projectData.conversationHistory.length, "messages");
                  safeSet(setConversationHistory, projectData.conversationHistory);
                } else {
                  console.log("No conversation history found in flat format for project", projectId);
                  safeSet(setConversationHistory, []);
                }
              }
            }
            // ✅ No conversation data found in either format
            else {
              safeSet(setConversationHistory, []);
              safeSet(setThreadId, null);
              safeSet(setRunId, null);
            }
          } else {
            console.log("Project document not found for", projectId);
            safeSet(setConversationHistory, []);
            safeSet(setThreadId, null);
            safeSet(setRunId, null);
          }
        } catch (error) {
          console.error("Error loading conversation data:", error);
          safeSet(setConversationHistory, []);
          safeSet(setThreadId, null);
          safeSet(setRunId, null);
        }
        
        // 🔍 Step 3: Debug logging (after cleanup and loading)
        if (projectId) {
          await logCurrentProjectConversationData();
        }
      }
    };

    loadProjectData();

    return () => {
      cancelled = true;
    };
  }, [projectId, isProjectSwitching]); // This will trigger when isProjectSwitching changes

  useEffect(() => {
    proposedColorsRef.current = proposedColors;
  }, [proposedColors]);

  useEffect(() => {
    const hints = t('hints', { returnObjects: true });
    if (hints && hints.length > 0) {
      setDefaultQuestion(hints[0]);
    }
  }, [t]);

  useEffect(() => {

    // Only notify parent when the background URL actually changed to avoid feedback loops
    if (backgroundImageUrl && lastSentBackgroundImageRef.current !== backgroundImageUrl) {
      setBackgroundImageUrlInApp(backgroundImageUrl);
      lastSentBackgroundImageRef.current = backgroundImageUrl;
    }

    // ✅ Only proceed if we have valid projectId and projectChangeKey
    if (projectChangeKey && projectId && projects) {
      const project = projects.find((p) => p.id === projectId);

      if (project && project.projectJson) {
        setIsProjectSwitching(true);
        
        try {
          const projectData = JSON.parse(project.projectJson);

          if (projectData && projectData.backgroundImage && projectData.backgroundImage.src) {
            handleProjectSwitch(projectData.backgroundImage.src);
          } else {
            console.warn("Project JSON does not contain a valid background image.");
            // ✅ Reset isProjectSwitching if no valid background image
            setIsProjectSwitching(false);
          }
        } catch (error) {
          console.error("Error parsing project JSON:", error);
          // ✅ Reset isProjectSwitching on error
          setIsProjectSwitching(false);
        }
      } else if (projectId && projects) {
        // For projects not found in the local array, check if this might be a new project
        // New projects don't need background switching or conversation loading
        
        // Only try Firebase loading if we have existing projects, indicating the system is loaded
        if (projects.length > 0) {
          // This might be a timing issue with existing projects
          let retryCount = 0;
          const maxRetries = 2;
          
          const loadProjectDirectly = async () => {
            try {
              const projectRef = doc(db, "usersProjects", projectId);
              const projectSnap = await getDoc(projectRef);
              
              if (projectSnap.exists()) {
                const projectDocData = projectSnap.data();
                
                // Check if project has projectJson and background image
                if (projectDocData.projectJson) {
                  const projectData = JSON.parse(projectDocData.projectJson);
                  if (projectData && projectData.backgroundImage && projectData.backgroundImage.src) {
                    setIsProjectSwitching(true);
                    handleProjectSwitch(projectData.backgroundImage.src);
                    return;
                  }
                }
                
                // Project exists but doesn't need background switching
                return;
              }
              
              // Project doesn't exist yet, retry once
              if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(loadProjectDirectly, 500);
                return;
              }
              
              // Final warning only for existing project scenarios
              console.warn("Project not found after retries:", projectId);
            } catch (error) {
              console.error("Error loading project directly:", error);
            }
          };
          
          setTimeout(loadProjectDirectly, 300);
        }
        // If projects.length === 0, this is likely initial load/new project - no action needed
        
        // ✅ Don't set isProjectSwitching to true if project not found
      }
    }
  }, [projectChangeKey, projects, projectId]);


  useEffect(() => {
    // ✅ Only proceed if we have valid projectId and required data
    if (projectChangeKey && !isProjectSwitching && projectId && projects && projects.length > 0) {
      const project = projects.find((p) => p.id === projectId);

      if (project) {
        // Extract projectScaleFactor from project data if available
        if (project.projectScaleFactor && setProjectScaleFactor) {
          setProjectScaleFactor(project.projectScaleFactor);
        }

        if (project.projectJson) {
          try {
            const projectData = JSON.parse(project.projectJson);

            // Get backgroundImage URL from project JSON
            const imageUrl = projectData.backgroundImage || projectData.canvasImage || null;
            const normalizedImageUrl = normalizeImageUrl(imageUrl);

            if (normalizedImageUrl) {
              if (normalizedImageUrl !== backgroundImageUrl) {
                setBackgroundImageUrl(normalizedImageUrl);
              }
            } else {
              console.warn("No background image URL found for the project.");
            }
          } catch (error) {
            console.error("Error parsing project JSON:", error);
          }
        }
      }
    }
  }, [projectChangeKey, projects, projectId, isProjectSwitching, setProjectScaleFactor]);




  useEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = modalBodyRef.current.scrollHeight;
    }
  }, [assistantResponse]);



  useEffect(() => {
    // Existing logic for processing assistantResponse

  }, [assistantResponse, t, i18n.language]);

  useEffect(() => {
    // console.log("Current Run ID in State:", runId);
  }, [runId]);

  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const initializeLanguage = () => {
    // Use i18next stored language if available, fallback to browser language
    let language = localStorage.getItem("i18nextLng") ||
      (navigator.language.startsWith("fr") ? "fr" : "en");

    // Ensure i18n is using the correct language
    i18n.changeLanguage(language);

    // Return the language to be used for `currentLanguage` in state
    return language;
  };


  // Define item mapping for English and French
  const itemMapping = {
    fr: {
      "porte": "doors",                   // Maps "porte" (singular) to "doors"
      "portes": "doors",                  // Maps "portes" (plural) to "doors"
      "fenêtres": "window",               // Maps "fenêtres" to "window"
      "fenetre": "window",                // Maps "fenetre" (singular) to "window"
      "fenetres": "window",               // Handles missing accents
      "porte de garage": "garagedoor",    // Maps "porte de garage" to "garagedoor"
      "portedegarage": "garagedoor"       // Handles no spaces
    },
    en: {
      "door": "doors",                    // Maps "door" (singular) to "doors"
      "doors": "doors",                   // Maps "doors" (plural) to "doors"  
      "windows": "window",                // Maps "windows" to "window"
      "window": "window",                 // Maps "window" to "window"
      "garagedoor": "garagedoor"          // Maps "garagedoor" to "garagedoor"
    }
  };

  const normalizeItemKey = (key) => {
    return key
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '') // Remove all spaces
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/s$/, ''); // Remove trailing "s" if present
  };

  const normalizeText = (text) => {
    return text
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '') // Remove spaces
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""); // Remove accents
  };

  const normalizeImageUrl = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && typeof value.src === 'string') return value.src;
    if (typeof value === 'object' && typeof value.url === 'string') return value.url;
    return '';
  };

  const toDataUrlIfNeeded = (base64OrDataUrl, mimeType = 'image/jpeg') => {
    if (!base64OrDataUrl || typeof base64OrDataUrl !== 'string') return null;
    if (base64OrDataUrl.startsWith('data:image/')) return base64OrDataUrl;
    return `data:${mimeType};base64,${base64OrDataUrl}`;
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result) {
        reject(new Error('invalid_data'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error('file_read_error'));
    reader.readAsDataURL(file);
  });

  const captureCurrentCanvasDesignImage = useCallback(() => {
    if (!fabricCanvas || typeof fabricCanvas.toDataURL !== 'function') {
      return '';
    }

    try {
      const backgroundObject = fabricCanvas.backgroundImage;
      const left = Number.isFinite(backgroundObject?.left) ? backgroundObject.left : 0;
      const top = Number.isFinite(backgroundObject?.top) ? backgroundObject.top : 0;
      const scaledWidth = typeof backgroundObject?.getScaledWidth === 'function'
        ? backgroundObject.getScaledWidth()
        : ((backgroundObject?.width || 0) * (backgroundObject?.scaleX || 1));
      const scaledHeight = typeof backgroundObject?.getScaledHeight === 'function'
        ? backgroundObject.getScaledHeight()
        : ((backgroundObject?.height || 0) * (backgroundObject?.scaleY || 1));

      if (scaledWidth > 0 && scaledHeight > 0) {
        return fabricCanvas.toDataURL({
          format: 'png',
          left,
          top,
          width: scaledWidth,
          height: scaledHeight,
          multiplier: 1,
        });
      }

      return fabricCanvas.toDataURL({
        format: 'png',
        multiplier: 1,
      });
    } catch (error) {
      console.error('Failed to capture current canvas design image:', error);
      return '';
    }
  }, [fabricCanvas]);

  const captureCanvasProductReferenceSheet = useCallback(async () => {
    if (!fabricCanvas || typeof fabricCanvas.getObjects !== 'function' || typeof fabricCanvas.toDataURL !== 'function') {
      return null;
    }

    const products = fabricCanvas.getObjects()
      .filter((object) => ['window', 'windows', 'door', 'doors', 'garagedoor', 'garagedoors'].includes(
        String(object?.itemType || '').toLowerCase().replace(/[\s_-]/g, '')
      ))
      .map((object) => ({
        object,
        bounds: typeof object.getBoundingRect === 'function' ? object.getBoundingRect(true, true) : null,
      }))
      .filter(({ bounds }) => bounds && bounds.width > 0 && bounds.height > 0)
      .sort((a, b) => a.bounds.top - b.bounds.top || a.bounds.left - b.bounds.left);

    if (!products.length) return null;

    try {
      const loadImage = (src) => new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });
      const crops = await Promise.all(products.map(async ({ object, bounds }, index) => {
        const horizontalPadding = Math.max(12, bounds.width * 0.18);
        const verticalPadding = Math.max(12, bounds.height * 0.14);
        const canvasWidth = typeof fabricCanvas.getWidth === 'function' ? fabricCanvas.getWidth() : fabricCanvas.width;
        const canvasHeight = typeof fabricCanvas.getHeight === 'function' ? fabricCanvas.getHeight() : fabricCanvas.height;
        const left = Math.max(0, bounds.left - horizontalPadding);
        const top = Math.max(0, bounds.top - verticalPadding);
        const width = Math.min(canvasWidth - left, bounds.width + horizontalPadding * 2);
        const height = Math.min(canvasHeight - top, bounds.height + verticalPadding * 2);
        const dataUrl = fabricCanvas.toDataURL({
          format: 'jpeg',
          quality: 0.92,
          multiplier: 2,
          left,
          top,
          width,
          height,
        });
        return {
          image: await loadImage(dataUrl),
          object,
          bounds,
          index: index + 1,
        };
      }));

      const columns = 2;
      const tileWidth = 500;
      const tileHeight = 230;
      const labelHeight = 30;
      const sheet = document.createElement('canvas');
      sheet.width = columns * tileWidth;
      sheet.height = Math.ceil(crops.length / columns) * tileHeight;
      const context = sheet.getContext('2d');
      if (!context) return null;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, sheet.width, sheet.height);

      crops.forEach(({ image, object, bounds, index }) => {
        const column = (index - 1) % columns;
        const row = Math.floor((index - 1) / columns);
        const tileX = column * tileWidth;
        const tileY = row * tileHeight;
        const itemType = String(object.itemType || '').toLowerCase().replace(/[\s_-]/g, '').includes('door') ? 'door' : 'window';
        const label = `${index}. ${itemType} style${object.windowType ? ` — ${object.windowType}` : ''}`;

        context.fillStyle = '#ffffff';
        context.fillRect(tileX, tileY, tileWidth, tileHeight);
        context.fillStyle = '#111827';
        context.font = '16px sans-serif';
        context.fillText(label.slice(0, 58), tileX + 12, tileY + 21);

        const maxWidth = tileWidth - 24;
        const maxHeight = tileHeight - labelHeight - 10;
        const scale = Math.min(maxWidth / image.width, maxHeight / image.height);
        const drawWidth = image.width * scale;
        const drawHeight = image.height * scale;
        context.drawImage(image, tileX + (tileWidth - drawWidth) / 2, tileY + labelHeight + (maxHeight - drawHeight) / 2, drawWidth, drawHeight);
      });

      return { dataUrl: sheet.toDataURL('image/jpeg', 0.9), itemCount: crops.length };
    } catch (error) {
      console.error('Failed to capture window and door close-up references:', error);
      return null;
    }
  }, [fabricCanvas]);

  const hasEditableCanvasItems = useCallback(() => {
    if (!fabricCanvas || typeof fabricCanvas.getObjects !== 'function') {
      return false;
    }

    const supportedItemTypes = new Set(['window', 'windows', 'door', 'doors', 'garagedoor', 'garagedoors', 'accessory']);

    const objectMatchesSupportedItem = (canvasObject) => {
      const normalizedItemType = String(
        canvasObject?.itemType ||
        canvasObject?.typeName ||
        canvasObject?.windowType ||
        ''
      )
        .toLowerCase()
        .replace(/\s+/g, '');

      if (supportedItemTypes.has(normalizedItemType)) {
        return true;
      }

      if (Array.isArray(canvasObject?._objects)) {
        return canvasObject._objects.some((childObject) => objectMatchesSupportedItem(childObject));
      }

      return false;
    };

    return fabricCanvas.getObjects().some((canvasObject) => objectMatchesSupportedItem(canvasObject));
  }, [fabricCanvas]);

  const hasRealModeGenerationSource = useCallback(() => {
    return referenceImages.length > 0 || hasEditableCanvasItems();
  }, [referenceImages.length, hasEditableCanvasItems]);

  const openMakeItRealNoDesignModal = useCallback(() => {
    setShowMakeItRealNoDesignVideo(false);
    setShowMakeItRealNoDesignModal(true);
  }, []);

  const closeMakeItRealNoDesignModal = useCallback(() => {
    setShowMakeItRealNoDesignModal(false);
    setShowMakeItRealNoDesignVideo(false);
  }, []);

  const handleReferenceImageChange = (event) => {
    const selectedFiles = Array.from(event?.target?.files || []);
    if (!selectedFiles.length) {
      return;
    }

    const maxReferenceImages = MAX_REFERENCE_IMAGES;
    const remainingSlots = Math.max(0, maxReferenceImages - referenceImages.length);
    if (remainingSlots <= 0) {
      toast.error(t('assistant.referenceImagesMaxReached', { defaultValue: 'Maximum 8 reference images.' }));
      event.target.value = '';
      return;
    }

    const filesToProcess = selectedFiles.slice(0, remainingSlots);
    if (selectedFiles.length > filesToProcess.length) {
      toast.info(t('assistant.referenceImagesTruncated', { defaultValue: 'Only the first 8 reference images are kept.' }));
    }

    const maxFileSizeBytes = 10 * 1024 * 1024;
    Promise.all(
      filesToProcess.map(async (file) => {
        if (!file.type?.startsWith('image/')) {
          throw new Error('invalid_type');
        }
        if (file.size > maxFileSizeBytes) {
          throw new Error('file_too_large');
        }

        const dataUrl = await readFileAsDataUrl(file);
        return {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
          name: file.name || 'reference-image',
          dataUrl,
        };
      })
    )
      .then((newImages) => {
        setReferenceImages((prev) => {
          const combined = [...prev, ...newImages];
          return combined.slice(0, maxReferenceImages);
        });
      })
      .catch((uploadError) => {
        const errorKey = uploadError?.message;
        if (errorKey === 'invalid_type') {
          toast.error(t('assistant.referenceImagesInvalidType', { defaultValue: 'Please select image files only.' }));
          return;
        }
        if (errorKey === 'file_too_large') {
          toast.error(t('assistant.referenceImagesMaxSize', { defaultValue: 'Each reference image must be smaller than 10 MB.' }));
          return;
        }
        toast.error(t('assistant.referenceImagesLoadError', { defaultValue: 'Failed to load reference images.' }));
      });

    event.target.value = '';
  };

  const removeReferenceImage = (imageId) => {
    setReferenceImages((prev) => prev.filter((image) => image.id !== imageId));
  };

  const clearReferenceImages = () => {
    setReferenceImages([]);
  };

  const openReferenceImagesPicker = () => {
    referenceFileInputRef.current?.click();
  };

  const addReferenceImageEntry = ({
    idPrefix = 'reference',
    name,
    dataUrl,
    sourceUrl = null,
    showToast = true,
    successMessage = null,
    referenceType = null,
    colorValue = null,
    promptHint = null
  }) => {
    const maxReferenceImages = MAX_REFERENCE_IMAGES;
    if (!dataUrl || typeof dataUrl !== 'string') {
      toast.error(t('assistant.referenceImagesLoadError', { defaultValue: 'Failed to load reference images.' }));
      return false;
    }

    const duplicateExists = referenceImages.some((image) =>
      (sourceUrl && image.sourceUrl && image.sourceUrl === sourceUrl) ||
      image.dataUrl === dataUrl
    );

    if (duplicateExists) {
      return true;
    }

    if (referenceImages.length >= maxReferenceImages) {
      toast.error(t('assistant.referenceImagesMaxReached', { defaultValue: 'Maximum 8 reference images.' }));
      return false;
    }

    const imageId = `${idPrefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setReferenceImages((prev) => [
      ...prev,
      {
        id: imageId,
        name: name || 'reference-image',
        dataUrl,
        sourceUrl,
        referenceType,
        colorValue,
        promptHint,
      },
    ]);

    if (showToast && successMessage) {
      toast.success(successMessage);
    }

    return true;
  };

  const addProjectThumbnailAsReference = (image, generatedIndex) => {
    if (!image?.url) {
      toast.error(t('assistant.referenceImagesLoadError', { defaultValue: 'Failed to load reference images.' }));
      return;
    }

    const isOriginal = image?.source === 'original';
    const isSaved = image?.source === 'saved';
    addReferenceImageEntry({
      idPrefix: isOriginal ? 'original-thumb' : (isSaved ? 'saved-thumb' : 'generated-thumb'),
      name: isOriginal
        ? t('assistant.originalProject', { defaultValue: 'Original project' })
        : (isSaved
          ? t('assistant.savedProjectLabel', { defaultValue: 'Saved project' })
          : t('assistant.generatedImageLabel', { defaultValue: 'Generated image {{count}}', count: generatedIndex })),
      dataUrl: image.url,
      sourceUrl: image.url,
      showToast: true,
      successMessage: t('assistant.thumbnailReferenceAdded', { defaultValue: 'Thumbnail added as a reference image.' }),
    });
  };

  const extractReferenceColorFromObject = (targetObject) => {
    if (!targetObject) {
      return null;
    }

    if (typeof targetObject.fill === 'string' && targetObject.fill.trim()) {
      return targetObject.fill.trim();
    }

    if (typeof targetObject.stroke === 'string' && targetObject.stroke.trim()) {
      return targetObject.stroke.trim();
    }

    const nestedObjects = Array.isArray(targetObject._objects) ? targetObject._objects : [];
    for (const nestedObject of nestedObjects) {
      if (typeof nestedObject?.fill === 'string' && nestedObject.fill.trim()) {
        return nestedObject.fill.trim();
      }

      if (typeof nestedObject?.stroke === 'string' && nestedObject.stroke.trim()) {
        return nestedObject.stroke.trim();
      }

      if (Array.isArray(nestedObject?.filters)) {
        const blendColorFilter = nestedObject.filters.find((filter) => filter?.type === 'BlendColor' && typeof filter?.color === 'string');
        if (blendColorFilter?.color) {
          return blendColorFilter.color.trim();
        }
      }
    }

    return null;
  };

  const buildColorSwatchReferenceDataUrl = (colorValue) => {
    if (!colorValue || typeof document === 'undefined') {
      return null;
    }

    const swatchCanvas = document.createElement('canvas');
    swatchCanvas.width = 320;
    swatchCanvas.height = 320;

    const context = swatchCanvas.getContext('2d');
    if (!context) {
      return null;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, swatchCanvas.width, swatchCanvas.height);

    context.fillStyle = colorValue;
    context.fillRect(28, 28, swatchCanvas.width - 56, swatchCanvas.height - 56);

    context.lineWidth = 6;
    context.strokeStyle = 'rgba(15, 23, 42, 0.12)';
    context.strokeRect(28, 28, swatchCanvas.width - 56, swatchCanvas.height - 56);

    return swatchCanvas.toDataURL('image/png');
  };

  const buildColorSamplePromptHint = (colorValue, colorLabel = null) => {
    const labelText = colorLabel ? ` named "${colorLabel}"` : '';
    return `The attached color sample${labelText} should be followed closely. Use RGB/HEX color ${colorValue} for the requested design elements where appropriate.`;
  };

  const addColorSwatchAsReference = (colorValue, colorLabel = null) => {
    if (!colorValue) {
      toast.error(t('assistant.referenceImagesLoadError', { defaultValue: 'Failed to load reference images.' }));
      return false;
    }

    const swatchDataUrl = buildColorSwatchReferenceDataUrl(colorValue);
    if (!swatchDataUrl) {
      toast.error(t('assistant.selectedItemReferenceCaptureFailed', { defaultValue: 'Could not capture the selected item as a reference image.' }));
      return false;
    }

    return addReferenceImageEntry({
      idPrefix: 'color-swatch',
      name: t('assistant.colorSwatchReferenceName', { defaultValue: 'Color sample' }),
      dataUrl: swatchDataUrl,
      sourceUrl: `color-swatch:${colorValue}`,
      showToast: true,
      referenceType: 'color-swatch',
      colorValue,
      promptHint: buildColorSamplePromptHint(colorValue, colorLabel),
      successMessage: t('assistant.colorSwatchReferenceAdded', { defaultValue: 'Color swatch added as a reference image.' }),
    });
  };

  const addSelectedCanvasItemAsReference = () => {
    if (!fabricCanvas || typeof fabricCanvas.getActiveObject !== 'function' || typeof fabricCanvas.toDataURL !== 'function') {
      toast.error(t('assistant.selectedItemReferenceUnavailable', { defaultValue: 'The selected item is not available right now.' }));
      return;
    }

    const activeObject = fabricCanvas.getActiveObject();
    if (!activeObject || typeof activeObject.getBoundingRect !== 'function') {
      toast.error(t('assistant.selectItemReferenceFirst', { defaultValue: 'Select a window, door, or garage door first.' }));
      return;
    }

    const normalizedItemType = String(activeObject.itemType || '').toLowerCase().replace(/\s+/g, '');
    const isSupportedItem = ['window', 'door', 'doors', 'garagedoor'].includes(normalizedItemType);

    if (!isSupportedItem) {
      const extractedColor = extractReferenceColorFromObject(activeObject);
      if (!extractedColor) {
        toast.error(t('assistant.selectedItemReferenceUnsupported', { defaultValue: 'Only windows, doors, garage doors, and color swatches can be used as reference images.' }));
        return;
      }

      addColorSwatchAsReference(extractedColor);
      return;
    }

    try {
      const objectBounds = activeObject.getBoundingRect(true, true);
      const canvasWidth = typeof fabricCanvas.getWidth === 'function' ? fabricCanvas.getWidth() : fabricCanvas.width;
      const canvasHeight = typeof fabricCanvas.getHeight === 'function' ? fabricCanvas.getHeight() : fabricCanvas.height;
      const padding = 28;
      const left = Math.max(0, objectBounds.left - padding);
      const top = Math.max(0, objectBounds.top - padding);
      const width = Math.min(canvasWidth - left, objectBounds.width + padding * 2);
      const height = Math.min(canvasHeight - top, objectBounds.height + padding * 2);

      const dataUrl = fabricCanvas.toDataURL({
        format: 'jpeg',
        quality: 0.92,
        multiplier: 1,
        left,
        top,
        width,
        height,
      });

      addReferenceImageEntry({
        idPrefix: 'selected-item',
        name: activeObject.windowType || t('assistant.selectedItemReferenceName', { defaultValue: 'Selected item' }),
        dataUrl,
        sourceUrl: `selected-item:${activeObject.windowType || activeObject.itemType || Date.now()}`,
        showToast: true,
        successMessage: t('assistant.selectedItemReferenceAdded', { defaultValue: 'Selected item added as a reference image.' }),
      });
    } catch (error) {
      console.error('Error capturing selected item as reference image:', error);
      toast.error(t('assistant.selectedItemReferenceCaptureFailed', { defaultValue: 'Could not capture the selected item as a reference image.' }));
    }
  };

  const getCurrentProjectImageUrl = () => {
    const canvasBackgroundImage = fabricCanvas?.backgroundImage;
    const imageUrlFromCanvas = canvasBackgroundImage?.getSrc ? canvasBackgroundImage.getSrc() : canvasBackgroundImage?.src;
    return normalizeImageUrl(selectedBaseImageUrl) || normalizeImageUrl(imageUrlFromCanvas) || normalizeImageUrl(backgroundImageUrl);
  };

  const getCurrentMonthKey = () => {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    return `${now.getFullYear()}-${month}`;
  };

  const isActiveStandardTrial = (data = userData) => {
    if (data?.standardTrialStatus !== 'active' || !data?.standardTrialEndsAt) {
      return false;
    }

    const rawEndsAt = data.standardTrialEndsAt;
    const endsAt = typeof rawEndsAt?.toDate === 'function' ? rawEndsAt.toDate() : new Date(rawEndsAt);
    return endsAt.getTime() > Date.now();
  };

  const getUserPlanForLimits = () => {
    const planName = userData?.planType || userData?.planName || 'Free';
    if (planName === 'standard_trial' && !isActiveStandardTrial()) {
      return 'Free';
    }
    return planName;
  };

  const resolveDesignerPlanLimits = (planName = null) => {
    const planToUse = planName || getUserPlanForLimits();
    const normalizedPlan = String(planToUse || 'Free');

    return (
      planLimits.designer[normalizedPlan] ||
      planLimits.designer[normalizedPlan.toLowerCase()] ||
      planLimits.designer[`${normalizedPlan.charAt(0).toUpperCase()}${normalizedPlan.slice(1).toLowerCase()}`] ||
      planLimits.designer.Free
    );
  };

  const getDesignerInteractionUsageStatus = () => {
    if (isAdminGuestTestingSession) {
      const weeklyCount = userData?.weeklyInteractionCount || 0;
      const monthlyCount = userData?.assistantInteractionCount || 0;

      return {
        canInteract: true,
        weeklyCount,
        monthlyCount,
        weeklyLimit: 'Unlimited',
        monthlyLimit: 'Unlimited',
        blockedPeriod: null,
        blockedCount: 0,
        blockedLimit: 'Unlimited',
      };
    }

    const interactionLimits = resolveDesignerPlanLimits();
    const weeklyCount = userData?.weeklyInteractionCount || 0;
    const monthlyCount = userData?.assistantInteractionCount || 0;
    const weeklyLimit = interactionLimits?.weeklyInteractions ?? 'Unlimited';
    const monthlyLimit = interactionLimits?.totalInteractions ?? 'Unlimited';

    const weeklyExceeded = weeklyLimit !== 'Unlimited' && weeklyCount >= weeklyLimit;
    const monthlyExceeded = monthlyLimit !== 'Unlimited' && monthlyCount >= monthlyLimit;

    return {
      canInteract: !weeklyExceeded && !monthlyExceeded,
      weeklyCount,
      monthlyCount,
      weeklyLimit,
      monthlyLimit,
      blockedPeriod: weeklyExceeded ? 'weekly' : 'monthly',
      blockedCount: weeklyExceeded ? weeklyCount : monthlyCount,
      blockedLimit: weeklyExceeded ? weeklyLimit : monthlyLimit,
    };
  };

  const getIncludedMonthlyGenerationsLimit = (planName = null) => {
    const planToUse = planName || getUserPlanForLimits();
    return planLimits.facadeAIGenerations[planToUse] ?? planLimits.facadeAIGenerations.Free;
  };

  const getExtraGenerationCreditsBalance = () => userData?.generationCreditsBalance || 0;

  const getFacadeAIMonthlyLimit = () => {
    return getIncludedMonthlyGenerationsLimit();
  };

  const getFacadeAICurrentMonthCount = () => {
    const currentMonth = getCurrentMonthKey();

    if (currentUser && currentUser.email.includes('guest')) {
      const storedMonth = localStorage.getItem('guest_facadeai_generation_month');
      if (storedMonth !== currentMonth) {
        return 0;
      }
      return parseInt(localStorage.getItem('guest_facadeai_generation_count') || '0', 10);
    }

    const storedMonth = userData?.facadeAIGenerationMonth;
    if (storedMonth !== currentMonth) {
      return 0;
    }

    return userData?.facadeAIGenerationCount || 0;
  };

  const checkFacadeAIGenerationLimit = (resolution = imageResolution) => {
    const cost = getGenerationCreditCost(resolution);
    if (isAdminGuestTestingSession) {
      return {
        limit: 'Unlimited',
        count: 0,
        isUnlimited: true,
        extraCreditsBalance: 0,
        extraCreditsLeft: 'Unlimited',
        includedCreditsLeft: Infinity,
        cost,
        canGenerate: true,
      };
    }

    const limit = getFacadeAIMonthlyLimit();
    const count = getFacadeAICurrentMonthCount();
    const extraCreditsBalance = getExtraGenerationCreditsBalance();
    return {
      limit,
      count,
      extraCreditsBalance,
      cost,
      ...getGenerationCreditUsage({ limit, count, balance: extraCreditsBalance, cost }),
    };
  };

  const incrementFacadeAIGenerationUsage = async (resolution) => {
    const currentMonth = getCurrentMonthKey();
    const cost = getGenerationCreditCost(resolution);

    if (isAdminGuestTestingSession) {
      return;
    }

    if (currentUser && currentUser.email.includes('guest')) {
      const storedMonth = localStorage.getItem('guest_facadeai_generation_month');
      const previousCount = storedMonth === currentMonth
        ? parseInt(localStorage.getItem('guest_facadeai_generation_count') || '0', 10)
        : 0;
      const guestLimit = getIncludedMonthlyGenerationsLimit('Guest');
      if (!getGenerationCreditUsage({ limit: guestLimit, count: previousCount, balance: 0, cost }).canGenerate) {
        throw new Error(t('assistant.insufficientGenerationCredits', { cost }));
      }
      const newCount = previousCount + cost;

      localStorage.setItem('guest_facadeai_generation_month', currentMonth);
      localStorage.setItem('guest_facadeai_generation_count', `${newCount}`);

      setUserData((prevData) => ({
        ...prevData,
        facadeAIGenerationMonth: currentMonth,
        facadeAIGenerationCount: newCount,
      }));
      return;
    }

    if (!currentUser || userRole === 'mainAdmin' || userRole === 'admin') {
      return;
    }

    const userRef = doc(db, 'users', currentUser.uid);

    let latestUserData = null;
    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) throw new Error('User profile not found');

      latestUserData = userDoc.data();
      const previousCount = latestUserData?.facadeAIGenerationMonth === currentMonth
        ? (latestUserData?.facadeAIGenerationCount || 0)
        : 0;
      const newCount = previousCount + cost;

      const planName = latestUserData?.planType || latestUserData?.planName || 'Free';
      const includedLimit = getIncludedMonthlyGenerationsLimit(
        planName === 'standard_trial' && !isActiveStandardTrial(latestUserData) ? 'Free' : planName
      );
      const currentCredits = latestUserData?.generationCreditsBalance || 0;
      const creditUsage = getGenerationCreditUsage({
        limit: includedLimit, count: previousCount, balance: currentCredits, cost,
      });
      if (!creditUsage.canGenerate) {
        throw new Error(t('assistant.insufficientGenerationCredits', { cost }));
      }

      const updatePayload = {
        facadeAIGenerationMonth: currentMonth,
        facadeAIGenerationCount: newCount,
      };

      if (creditUsage.purchasedCreditsToUse > 0) {
        updatePayload.generationCreditsBalance = currentCredits - creditUsage.purchasedCreditsToUse;
      }

      transaction.update(userRef, updatePayload);
    });

    if (latestUserData) {
      const previousCount = latestUserData?.facadeAIGenerationMonth === currentMonth
        ? (latestUserData?.facadeAIGenerationCount || 0)
        : 0;
      const newCount = previousCount + cost;
      const planName = latestUserData?.planType || latestUserData?.planName || 'Free';
      const includedLimit = getIncludedMonthlyGenerationsLimit(
        planName === 'standard_trial' && !isActiveStandardTrial(latestUserData) ? 'Free' : planName
      );
      const balance = latestUserData?.generationCreditsBalance || 0;
      const { purchasedCreditsToUse } = getGenerationCreditUsage({
        limit: includedLimit, count: previousCount, balance, cost,
      });

      setUserData((prevData) => ({
        ...prevData,
        facadeAIGenerationMonth: currentMonth,
        facadeAIGenerationCount: newCount,
        generationCreditsBalance: balance - purchasedCreditsToUse,
      }));
    }
  };

  const pollFacadeAIJob = async (jobId, resolution) => {
    const maxAttempts = 90;
    const pollDelayMs = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const statusResponse = await fetch(`/.netlify/functions/facadeaiGenerateStatusProxy?jobId=${encodeURIComponent(jobId)}`);

      if (!statusResponse.ok) {
        throw new Error(`Status check failed (${statusResponse.status})`);
      }

      const statusData = await statusResponse.json();
      const statusValue = (statusData?.status || '').toString().toLowerCase();

      if (['done', 'completed'].includes(statusValue)) {
        if (resolution !== '1K') {
          const minimumLongEdge = resolution === '4K' ? 4096 : 2048;
          if (Math.max(statusData.outputWidth || 0, statusData.outputHeight || 0) < minimumLongEdge) {
            throw new Error(t('assistant.generationResolutionMismatch', { resolution }));
          }
        }
        let generatedImage = toDataUrlIfNeeded(statusData.image_data, statusData.imageMimeType);
        if (statusData.image_url) {
          const imageResponse = await fetch(statusData.image_url);
          if (!imageResponse.ok) {
            throw new Error(`Generated image download failed (${imageResponse.status})`);
          }
          generatedImage = await readFileAsDataUrl(await imageResponse.blob());
        }
        if (!generatedImage) {
          throw new Error('Generation completed but no image was returned');
        }
        return generatedImage;
      }

      if (['error', 'failed'].includes(statusValue)) {
        throw new Error(statusData?.error || 'FacadeAI generation failed');
      }

      await new Promise((resolve) => setTimeout(resolve, pollDelayMs));
    }

    throw new Error('FacadeAI generation timed out');
  };

  const getAutomaticRealismPrompt = useCallback(() => (
    i18n.language?.startsWith('fr')
      ? "Utilisez la photo de la maison comme image de base et préservez son architecture, son angle de vue, son cadrage, son environnement, son aménagement paysager ainsi que l'emplacement et la taille de chaque ouverture. Les références fournissent uniquement le style; ignorez les positions et dimensions qui y sont dessinées, car elles peuvent être incorrectes. La photo de la maison détermine seule l'emplacement et la taille des ouvertures. Traitez les dessins de fenêtres et de portes fournis comme des spécifications exactes de produit, même s'il s'agit d'illustrations à plat. Pour chaque ouverture, reproduisez fidèlement le modèle correspondant : couleur et profil du cadre, nombre et disposition des vantaux ou panneaux distincts dans chaque ouverture (ne fusionnez pas plusieurs vantaux en un seul), petits-bois, traverses, montants et surtout motif exact du vitrage décoratif, nombre de carreaux et leurs proportions. Ne remplacez pas un vitrage de porte distinctif par du verre uni ou un motif générique. Transformez les dessins en fenêtres et portes installées réalistes, avec une profondeur de cadre, des joints de pose et des ombres de contact réalistes. Chaque cadre doit s’ajuster exactement aux limites de son ouverture : mêmes largeur, hauteur, proportions, alignement de l’allège et du linteau, et perspective; il doit reposer naturellement contre le plan du mur, aligné avec le cadre extérieur, sans flotter, dépasser, recouvrir le revêtement ou laisser de jour. Pour les portes d’entrée, utilisez toute la hauteur prévue, du seuil jusqu’à près du linteau, et toute la largeur prévue; ne réduisez pas le vantail et ne laissez pas une grande bordure vide autour. Pour les vitres ordinaires, utilisez un verre véritablement clair, transparent et neutre, avec des reflets nets mais modérés du ciel ensoleillé et des alentours; la vue à travers chaque vitre doit rester claire. Ne donnez pas au verre clair un aspect dépoli, laiteux, embué, gravé, texturé, opaque ou flou. N’inventez pas d’objets intérieurs visibles. Ne conservez un vitrage décoratif ou obscurci que là où le modèle le montre explicitement; préservez exactement chaque division du vitrage. Rendez toute la maison comme une seule photo réaliste cohérente avec l'environnement existant. N'ajoutez, ne supprimez, ne déplacez, ne redimensionnez, ne fusionnez, ne divisez et ne réorganisez aucune ouverture; gardez le reste de la façade et les alentours fidèles à la photo de base."
      : 'Use the house photo as the base. It defines the exact boundaries, dimensions, positions, wall depth, and perspective of every opening. Fit each replacement exactly within its existing opening and align the frame naturally to the wall and trim. For entry doors, use the full designed door height from threshold to near the head jamb and the full intended width; do not shrink the door leaf or leave a large blank border around it. Use the full-facade template and close-ups for product style only. Ignore any opening positions, sizes, and alignment shown in the template; use the house photo alone to determine every real opening. Copy the exact frame color and finish for each individual window and door, along with its frame profile, sash count and arrangement, and glass design. Preserve the exact number of separate window sash units inside each outer opening; keep multiple adjacent sashes separate rather than merging them into one wide sash. Preserve window frame colors independently from the doors: do not copy dark or black door frames onto windows, and do not darken or recolor any frame. Do not transfer other door details to windows or make different units uniform. Never invent, add, remove, or alter muntins, grilles, transoms, rails, stiles, or glass divisions. Keep plain panes plain; where panes are divided, preserve the exact number, shape, placement, and proportions of divisions shown in the reference. Do not modernize, simplify, or substitute a generic style. Turn the flat drawings into realistic installed products with frame depth, installation joints, and contact shadows. For ordinary window glass, use genuinely clear, transparent, color-neutral glazing with crisp but restrained reflections of the sunny blue sky and nearby surroundings; keep the view through each pane visibly clear. Do not make clear glass frosted, milky, fogged, etched, textured, opaque, or diffusely blurred. Do not invent prominent interior objects. Preserve decorative or obscured glass only where the design reference explicitly shows it, and keep every glass division exactly. Preserve the house, camera, landscaping, and surroundings, and render the whole scene as one coherent realistic photograph.'
  ), [i18n.language]);

  const executeFacadeAIGeneration = useCallback(async ({
    projectImageUrl,
    prompt,
    referenceImagesPayload,
    skipPreSave = false,
    closeDesignerOnSuccess = true,
    resolution = imageResolution,
  }) => {
    if (isFacadeAIGenerating || isLoading) {
      return false;
    }

    const hasReferencePayload = Array.isArray(referenceImagesPayload) && referenceImagesPayload.length > 0;

    if (!hasEditableCanvasItems() && !hasReferencePayload) {
      openMakeItRealNoDesignModal();
      return false;
    }

    const usageStatus = checkFacadeAIGenerationLimit(resolution);
    if (!usageStatus.canGenerate) {
      toast.error(
        t('assistant.insufficientGenerationCredits', { cost: usageStatus.cost })
      );
      return false;
    }

    if (!projectImageUrl) {
      toast.error(t('assistant.noImage'));
      return false;
    }

    if (!skipPreSave && !isGuestUser && isOriginalProjectBaseActive) {
      const shouldSaveProjectFirst = window.confirm(
        t('assistant.saveCurrentProjectFirstConfirm', {
          defaultValue: 'Save the current project first?',
        })
      );

      if (shouldSaveProjectFirst && typeof saveUpdate === 'function') {
        try {
          const didSaveProject = await saveUpdate();
          if (!didSaveProject) {
            toast.error(
              t('assistant.saveBeforeGenerateFailed', {
                defaultValue: 'Could not save the current project. Generation cancelled.',
              })
            );
            return false;
          }
        } catch (saveError) {
          console.error('Error saving project before FacadeAI generation:', saveError);
          toast.error(
            t('assistant.saveBeforeGenerateFailed', {
              defaultValue: 'Could not save the current project. Generation cancelled.',
            })
          );
          return false;
        }
      }
    }

    setUserInput('');
    setWorkflowMode('real');
    setWorkflowProgress('');
    setWorkflowModalOpen(true);
    setDesignerModalOpen(false);
    setIsFacadeAIGenerating(true);
    setFacadeAIGenerationStartedAt(Date.now());
    setFacadeAIGenerationElapsedSeconds(0);
    setStatusMessage(i18n.language?.startsWith('fr') ? 'Génération FacadeAI en cours...' : 'FacadeAI generation in progress...');

    try {
      console.log('[facadeai] generate-request', {
        prompt,
        projectImageUrl,
        referenceImagesCount: referenceImagesPayload?.length || 0,
        triggeredFrom: skipPreSave ? 'menu-shortcut' : 'designer-real-mode',
        language: i18n.language?.startsWith('fr') ? 'fr' : 'en',
      });

      const startResponse = await fetch('/.netlify/functions/facadeaiGenerateProxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageUrl: projectImageUrl,
          prompt,
          userLanguage: i18n.language?.startsWith('fr') ? 'fr' : 'en',
          userId: currentUser?.uid || null,
          sourceApp: 'FacadeWebApp',
          imageResolution: resolution,
          referenceImages: referenceImagesPayload?.length ? referenceImagesPayload : undefined
        })
      });

      if (!startResponse.ok) {
        const startError = await startResponse.json().catch(() => null);
        throw new Error(startError?.error || `Failed to start generation (${startResponse.status})`);
      }

      const startData = await startResponse.json();
      const jobId = startData?.jobId;

      if (!jobId) {
        throw new Error('No jobId returned by FacadeAI generate endpoint');
      }

      const generatedImageDataUrl = await pollFacadeAIJob(jobId, resolution);
      await incrementFacadeAIGenerationUsage(resolution);

      if (typeof onFacadeAIGeneratedImage === 'function') {
        try {
          await onFacadeAIGeneratedImage(generatedImageDataUrl);
        } catch (generatedImagePersistError) {
          console.error('Error persisting generated image after successful generation:', generatedImagePersistError);
        }
      }

      clearReferenceImages();
      setStatusMessage(t('assistant.generatedImageReady', { defaultValue: 'FacadeAI generated image is ready.' }));
      if (closeDesignerOnSuccess) {
        setDesignerModalOpen(false);
      }
      toast.success(t('assistant.generationComplete', { defaultValue: 'FacadeAI generation complete' }));
      return true;
    } catch (error) {
      console.error('Error generating with FacadeAI:', error);
      setStatusMessage(t('assistant.generationError', { defaultValue: 'FacadeAI generation error.' }));
      toast.error(error?.message || t('assistant.generationFailed', { defaultValue: 'FacadeAI generation failed' }));
      return false;
    } finally {
      setIsFacadeAIGenerating(false);
      setWorkflowModalOpen(false);
      setFacadeAIGenerationStartedAt(null);
      setFacadeAIGenerationElapsedSeconds(0);
    }
  }, [
    checkFacadeAIGenerationLimit,
    currentUser,
    i18n.language,
    incrementFacadeAIGenerationUsage,
    imageResolution,
    isAdminGuestTestingSession,
    isFacadeAIGenerating,
    isGuestUser,
    hasEditableCanvasItems,
    isLoading,
    isOriginalProjectBaseActive,
    openMakeItRealNoDesignModal,
    onFacadeAIGeneratedImage,
    pollFacadeAIJob,
    saveUpdate,
    setDesignerModalOpen,
    t
  ]);

  const handleQuickGenerateWithFacadeAI = useCallback(async () => {
    if (isFacadeAIGenerating || isLoading || resolutionChoiceRef.current) return false;
    const resolution = await new Promise((resolve) => {
      resolutionChoiceRef.current = resolve;
      setShowResolutionModal(true);
    });
    if (!resolution) return false;

    const baseOriginalImageUrl = normalizeImageUrl(originalProjectImageUrl) || getCurrentProjectImageUrl();
    if (!baseOriginalImageUrl) {
      toast.error(t('assistant.noImage', { defaultValue: 'No image available.' }));
      return false;
    }

    if (!hasEditableCanvasItems()) {
      openMakeItRealNoDesignModal();
      return false;
    }

    const currentCanvasDesignImage = captureCurrentCanvasDesignImage();
    if (!currentCanvasDesignImage) {
      toast.error(t('assistant.referenceImagesLoadError', { defaultValue: 'Failed to load reference images.' }));
      return false;
    }

    const productReferenceSheet = await captureCanvasProductReferenceSheet();

    const quickReferenceImages = [{
      dataUrl: currentCanvasDesignImage,
      name: 'current-canvas-design',
      referenceType: 'design',
      promptHint: i18n.language?.startsWith('fr')
        ? 'Référence principale de style : le canevas actuel de la façade. Utilisez-le uniquement pour l’apparence des produits; ignorez les positions, dimensions et alignements des ouvertures qui y sont dessinés, car ils peuvent être inexacts. La photo de la maison détermine seule l’emplacement et la taille des vraies ouvertures. Copiez le style exact des fenêtres et portes, la disposition des vantaux et toutes les divisions du vitrage. N’inventez aucune division.'
        : 'Primary style reference: the current full-facade canvas. Use it only for product appearance; ignore the opening positions, sizes, and alignment drawn in it because they may be inaccurate. The house photo alone determines the real opening locations and dimensions. Copy the exact window and door styles, sash arrangements, and glass divisions. Do not invent divisions.',
    }, ...(productReferenceSheet ? [{
      dataUrl: productReferenceSheet.dataUrl,
      name: 'Window and door design close-ups',
      referenceType: 'design-details',
      promptHint: i18n.language?.startsWith('fr')
        ? `Planche de gros plans de ${productReferenceSheet.itemCount} produits dessinés dans le canevas, numérotés uniquement pour identifier leur style. Ces vues montrent le style, pas leur emplacement réel. Utilisez la photo de la maison pour positionner les produits; reproduisez exactement les détails des gros plans.`
        : `Close-up sheet of ${productReferenceSheet.itemCount} canvas products, numbered for style identification only. These views show product styles, not their real positions. Use the house photo to place products; reproduce the close-up details exactly.`,
    }] : [])];

    return executeFacadeAIGeneration({
      projectImageUrl: baseOriginalImageUrl,
      prompt: getAutomaticRealismPrompt(),
      referenceImagesPayload: quickReferenceImages,
      skipPreSave: true,
      closeDesignerOnSuccess: false,
      resolution,
    });
  }, [
    captureCanvasProductReferenceSheet,
    captureCurrentCanvasDesignImage,
    executeFacadeAIGeneration,
    getAutomaticRealismPrompt,
    getCurrentProjectImageUrl,
    hasEditableCanvasItems,
    i18n.language,
    isFacadeAIGenerating,
    isLoading,
    openMakeItRealNoDesignModal,
    originalProjectImageUrl,
    t
  ]);

  useEffect(() => {
    if (!onReferenceImageActionReady) {
      return undefined;
    }

    onReferenceImageActionReady({
      addSelectedItemAsReferenceImage: () => addSelectedCanvasItemAsReference(),
      addColorSwatchReferenceImage: (colorValue, colorLabel) => addColorSwatchAsReference(colorValue, colorLabel),
      generateRealisticImageFromCanvas: () => handleQuickGenerateWithFacadeAI()
    });

    return () => {
      onReferenceImageActionReady(null);
    };
  }, [onReferenceImageActionReady, handleQuickGenerateWithFacadeAI]);

  const handleGenerateWithFacadeAI = async () => {
    if (!hasRealModeGenerationSource()) {
      openMakeItRealNoDesignModal();
      return;
    }

    const trimmedUserPrompt = userInput?.trim() || '';

    if (!trimmedUserPrompt) {
      toast.warning(
        t('assistant.realModePromptRequired', {
          defaultValue: 'To get a generation please enter your request first.',
        })
      );
      return;
    }

    const generationAckMessage = i18n.language?.startsWith('fr')
      ? '🎨 Requête de génération FacadeAI envoyée.'
      : '🎨 FacadeAI generation request sent.';

    setConversationHistory((prev) => {
      const questionEntry = { question: trimmedUserPrompt, response: generationAckMessage };
      const newHistory = [questionEntry, ...prev];

      if (threadId) {
        saveProjectConversationData(threadId, runId, newHistory, imageFileId).catch((error) =>
          console.error('Error saving FacadeAI prompt to conversation history:', error)
        );
      }

      return newHistory;
    });

    let referenceImagesPayload = referenceImages.length
      ? referenceImages.map((image) => ({
        dataUrl: image.dataUrl,
        name: image.name,
        referenceType: image.referenceType,
        colorValue: image.colorValue,
        promptHint: image.promptHint,
      }))
      : [];

    const hasCanvasDesignReference = referenceImagesPayload.some((image) => image.referenceType === 'design');
    if (hasEditableCanvasItems() && !hasCanvasDesignReference) {
      const currentCanvasDesignImage = captureCurrentCanvasDesignImage();
      if (!currentCanvasDesignImage) {
        toast.error(t('assistant.referenceImagesLoadError', { defaultValue: 'Failed to load reference images.' }));
        return;
      }

      if (isGuestUser && typeof saveUpdate === 'function') {
        try {
          await saveUpdate();
        } catch (saveError) {
          console.error('Error creating guest design snapshot before FacadeAI generation:', saveError);
        }
      }

      const canvasDesignReference = {
        dataUrl: currentCanvasDesignImage,
        name: 'Current canvas design (full facade)',
        referenceType: 'design',
        promptHint: i18n.language?.startsWith('fr')
          ? "Référence principale de style : le canevas actuel de la façade. Utilisez-le uniquement pour l’apparence des produits; ignorez les positions, dimensions et alignements des ouvertures qui y sont dessinés, car ils peuvent être inexacts. La photo de la maison détermine seule l’emplacement et la taille des vraies ouvertures. Copiez exactement le style des fenêtres et portes, la disposition des vantaux et toutes les divisions du vitrage. N’inventez aucune division."
          : 'Primary style reference: the current full-facade canvas. Use it only for product appearance; ignore the opening positions, sizes, and alignment drawn in it because they may be inaccurate. The house photo alone determines the real opening locations and dimensions. Copy the exact window and door styles, sash arrangements, and glass divisions. Do not invent divisions.',
      };

      // Keep the full-facade design reference even when the user has attached several other references.
      referenceImagesPayload = [canvasDesignReference, ...referenceImagesPayload].slice(0, MAX_REFERENCE_IMAGES);
    }

    const productReferenceSheet = hasEditableCanvasItems()
      ? await captureCanvasProductReferenceSheet()
      : null;
    if (productReferenceSheet) {
      const detailReference = {
        dataUrl: productReferenceSheet.dataUrl,
        name: 'Window and door design close-ups',
        referenceType: 'design-details',
        promptHint: i18n.language?.startsWith('fr')
          ? `Planche de gros plans de ${productReferenceSheet.itemCount} produits dessinés dans le canevas, numérotés uniquement pour identifier leur style. Ces vues montrent le style, pas leur emplacement réel. Utilisez la photo de la maison pour positionner les produits; reproduisez exactement les détails des gros plans.`
          : `Close-up sheet of ${productReferenceSheet.itemCount} canvas products, numbered for style identification only. These views show product styles, not their real positions. Use the house photo to place products; reproduce the close-up details exactly.`,
      };
      const currentReferences = referenceImagesPayload.filter((image) => image.referenceType !== 'design-details');
      referenceImagesPayload = [currentReferences[0], detailReference, ...currentReferences.slice(1)]
        .filter(Boolean)
        .slice(0, MAX_REFERENCE_IMAGES);
    }

    setUserInput('');
    await executeFacadeAIGeneration({
      projectImageUrl: getCurrentProjectImageUrl(),
      prompt: trimmedUserPrompt,
      referenceImagesPayload,
      skipPreSave: false,
      closeDesignerOnSuccess: true,
    });
  };





  // Updated processAssistantResponse function in FacadeDesigner.js
  const processAssistantResponse = (response) => {
    // Try both French and English patterns to be safe (HTML and plain text)
    const frenchSummaryRegex = /<h3>\s*Résumé des Couleurs Proposées\s*<\/h3>\s*<p>([\s\S]*?)<\/p>/i;
    const englishSummaryRegex = /<h3>\s*Summary of Proposed Colors\s*<\/h3>\s*<p>([\s\S]*?)<\/p>/i;
    const frenchPlainSummaryRegex = /Résumé des Couleurs Proposées\s*[\r\n]+([\s\S]*?)(?=\n\s*(Analyse de la Façade|Recommandations|Facade Analysis|Recommendations|$))/i;
    const englishPlainSummaryRegex = /Summary of Proposed Colors\s*[\r\n]+([\s\S]*?)(?=\n\s*(Analyse de la Façade|Recommandations|Facade Analysis|Recommendations|$))/i;
    const genericFrenchHtmlSummaryRegex = /<p>\s*((?:Fenêtres|Portes)\s*:[\s\S]*?)<\/p>/i;
    const genericEnglishHtmlSummaryRegex = /<p>\s*((?:Windows|Doors)\s*:[\s\S]*?)<\/p>/i;
    const genericFrenchPlainSummaryRegex = /((?:Fenêtres|Portes)\s*:[^\n]+(?:\s*<br\/?>|\n)+(?:Fenêtres|Portes)\s*:[^\n<]+)/i;
    const genericEnglishPlainSummaryRegex = /((?:Windows|Doors)\s*:[^\n]+(?:\s*<br\/?>|\n)+(?:Windows|Doors)\s*:[^\n<]+)/i;
    
    let match = response.match(frenchSummaryRegex);
    let detectedLanguage = 'fr';
    
    if (!match) {
      match = response.match(englishSummaryRegex);
      detectedLanguage = 'en';
    }

    if (!match) {
      match = response.match(frenchPlainSummaryRegex);
      detectedLanguage = 'fr';
    }

    if (!match) {
      match = response.match(englishPlainSummaryRegex);
      detectedLanguage = 'en';
    }

    if (!match) {
      match = response.match(genericFrenchHtmlSummaryRegex);
      detectedLanguage = 'fr';
    }

    if (!match) {
      match = response.match(genericEnglishHtmlSummaryRegex);
      detectedLanguage = 'en';
    }

    if (!match) {
      match = response.match(genericFrenchPlainSummaryRegex);
      detectedLanguage = 'fr';
    }

    if (!match) {
      match = response.match(genericEnglishPlainSummaryRegex);
      detectedLanguage = 'en';
    }

    if (match && match[1]) {
      const summary = match[1];

      // Split the summary into pairs
      const itemColorPairs = summary.split(/<br\s*\/?>|\n/).map(pair => pair.trim()).filter(Boolean);
      const combinedColors = {};

      itemColorPairs.forEach(pair => {
        const [item, colors] = pair.split(/:\s*(.+)/);
        if (item && colors) {
          // ✅ Keep original color names with spaces for getColorByName()
          const colorList = colors
            .split(detectedLanguage === 'fr' ? ' et ' : ' and ')
            .map(color => color.trim()); // Keep original names like "Grey Beige", "Burgundy"

          const itemKey = normalizeToCamelCase(item);

          // Translate the item based on detected language and mapping
          let translatedItem;
          if (itemMapping[detectedLanguage]) {
            const normalizedKey = normalizeToCamelCase(itemKey);
            translatedItem = itemMapping[detectedLanguage][normalizedKey] || normalizedKey;
          } else {
            translatedItem = itemKey; // Fallback to the original itemKey
          }

          console.log("✅ Extracted colors for", translatedItem, ":", colorList);

          combinedColors[translatedItem] = colorList;
        }
      });

      // Update state with normalized and translated colors
      setProposedColors(prevColors => ({ ...prevColors, ...combinedColors }));

      // Create buttons for proposed colors
      const newColorButtons = Object.keys(combinedColors).flatMap(item => {
        const colors = combinedColors[item];
        return colors.length === 1 ? [`${item} ${colors[0]}`] : colors.map(color => `${item} ${color}`);
      });

      const nextButtons = Object.values(combinedColors).every(colors => colors.length === 1) ? ["Show Me"] : newColorButtons;
      setColorButtons(nextButtons);
      // console.log("Combined Colors:", combinedColors);
      // console.log("Color Buttons:", newColorButtons);
      
    } else {
      console.warn("❌ Regex match failed or no color summary found in assistant response.");
      console.log("🔍 Full response text to debug:", response);
      
      // ✅ Try alternative patterns for debugging
      const frenchSummaryPattern = /Résumé des Couleurs Proposées/i;
      const englishSummaryPattern = /Summary of Proposed Colors/i;
      
      if (frenchSummaryPattern.test(response)) {
        console.log("✅ Found French summary text, but HTML structure might be different");
      } else if (englishSummaryPattern.test(response)) {
        console.log("✅ Found English summary text, but HTML structure might be different");
      } else {
        console.log("❌ No summary text found at all");
      }
      
      // ✅ Clear color buttons if no valid color summary is found
      setColorButtons([]);
      setProposedColors({});
    }
    
    // Complete processing-request step when response is received and move to recommendations-ready
    // This should happen regardless of whether we found color recommendations
    if (isStepActive('processing-request')) {
      updateAppState({ aiLoading: false });
      completeStep('processing-request');
      console.log('🎯 OnboardingDebug: Completed processing-request step - AI response received, moving to recommendations-ready');
    }
  };




  const itemNameMapping = {
    "porte": "door",
    "portes": "door",
    "fenêtre": "windows",
    "fenêtres": "windows",
    "window": "windows",
    "garage door": "garagedoor", // Standardize "garage door" as "garagedoor" without space
    "garagedoor": "garagedoor"
  };

  // Function to normalize item names based on common variations
  const normalizeItemName = (name) => {
    const formattedName = name.toLowerCase().trim(); // Convert to lowercase and trim whitespace
    return itemNameMapping[formattedName] || formattedName; // Return the mapped name or the original if not mapped
  };
  const uploadImage = async (imageUrl) => {
    try {
      setStatusMessage(t('assistant.statusUploading'));

      const backgroundImage = fabricCanvas.backgroundImage;
      const imageUrlFromCanvas = backgroundImage?.getSrc ? backgroundImage.getSrc() : backgroundImage?.src;
      const chosenImageUrl = imageUrl || imageUrlFromCanvas;

      // ✅ Ensure we have a string URL, not a Fabric object
      const finalImageUrl = typeof chosenImageUrl === 'string' ? chosenImageUrl : chosenImageUrl?.src;
      
      if (!finalImageUrl || typeof finalImageUrl !== 'string') {
        console.error('❌ Invalid image URL:', chosenImageUrl);
        throw new Error('No valid image URL available for upload.');
      }

      // Upload the image to OpenAI and get the file ID
      const data = await uploadImageToOpenAI(finalImageUrl);

      if (!data) {
        throw new Error('No file ID returned from OpenAI image upload.');
      }

      // Trigger detections (RoboFlow detection works independently of AI evaluation)
      const predictions = await triggerRoboflowDetections(finalImageUrl);
      if (!predictions || predictions.length === 0) {
        console.warn('No valid predictions found or no doors or windows in image.');
      } else {
        setRoboflowFoundDetections(predictions); // Save detections to state if found
        window.roboflowDetectionsForGPT = predictions; // Make available for GPT-4o call
      }

      return data; // Return file ID for OpenAI interaction
    } catch (error) {
      console.error('Error in uploadImage:', error.message);
      setStatusMessage(t('assistant.statusError'));
      throw error;
    }
  };

  const handleOnlyDetections = async () => {
    try {
      // Notify the user that detection has started
      setStatusMessage(t("assistant.statusUploading"));

      // Retrieve the image URL from the canvas
      const backgroundImage = fabricCanvas.backgroundImage;
      const imageUrlFromCanvas = backgroundImage?.getSrc ? backgroundImage.getSrc() : backgroundImage?.src;

      // ✅ Ensure we have a string URL, not a Fabric object
      const finalImageUrl = typeof imageUrlFromCanvas === 'string' ? imageUrlFromCanvas : imageUrlFromCanvas?.src;

      if (!finalImageUrl || typeof finalImageUrl !== 'string') {
        console.error('❌ Invalid image URL:', imageUrlFromCanvas);
        throw new Error("No valid image URL available for detection.");
      }

      console.log('🔍 Using image URL for detection:', finalImageUrl);

      // Trigger Roboflow detection
      const predictions = await triggerRoboflowDetections(finalImageUrl);

      if (!predictions || predictions.length === 0) {
        console.warn("No valid predictions found.");
        return;
      }

      setRoboflowFoundDetections(predictions)

      // console.log("detections found", predictions)
      // Save detections to state
      setRoboflowFoundDetections(predictions);
      window.roboflowDetectionsForGPT = predictions; // Make available for GPT-4o call

      // Call the App-level detection function
      await triggerAIDetectionForOpenedProject();

      // Notify the user of completion
      setStatusMessage(t("assistant.statusComplete"));
    } catch (error) {
      console.error("Error during detection:", error.message);
      setStatusMessage(t("assistant.statusError"));
    }
  };

  // Helper Function: Preprocess Image for Gemini
  const preprocessImageForGemini = async (imageUrl) => {
    const targetMaxSize = 3072;
    const targetMinSize = 768;

    // Load the image
    const img = await loadImage(imageUrl);

    // Calculate the scaling factor
    const scaleFactor = calculateScalingFactor(img.width, img.height, targetMaxSize, targetMinSize);

    // Resize the image
    const scaledImage = resizeImage(img, scaleFactor);

    return { scaledImage, scaleFactor };
  };

  // Helper Function: Calculate Scaling Factor
  const calculateScalingFactor = (imageWidth, imageHeight, maxSize, minSize) => {
    const widthScale = maxSize / imageWidth;
    const heightScale = maxSize / imageHeight;
    const scale = Math.min(widthScale, heightScale);

    // If the image is smaller than the minimum size, scale it up
    return Math.max(scale, minSize / Math.max(imageWidth, imageHeight));
  };

  // Helper Function: Resize Image
  const resizeImage = (image, scaleFactor) => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.width * scaleFactor);
    canvas.height = Math.round(image.height * scaleFactor);

    const context = canvas.getContext("2d");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL(); // Convert to base64
  };

  // Helper Function: Load Image from URL
  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous"; // Avoid CORS issues
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = url;
    });
  };


  // const triggerGeminiDetections = async (imageUrl) => {
  //   try {
  //     const response = await fetch('/.netlify/functions/geminiDetections', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ imageUrl }),
  //     });

  //     if (!response.ok) {
  //       throw new Error(`Gemini API error: ${response.status}`);
  //     }

  //     // Parse the response
  //     const data = await response.json();

  //     if (!data || !data.boundingBoxes) {
  //       console.warn("No bounding boxes found in Gemini response.");
  //       return { boundingBoxes: [], scaleFactor: 1 }; // Default empty response
  //     }


  //     // Return boundingBoxes and scaleFactor
  //     return data;
  //   } catch (error) {
  //     console.error("Error triggering Gemini detections:", error);
  //     return { boundingBoxes: [], scaleFactor: 1 }; // Return defaults on error
  //   }
  // };

  // const triggerVisionDetections = async (imageUrl) => {
  //   try {
  //     const response = await fetch('/.netlify/functions/roboflowDetections', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ imageUrl }),
  //     });

  //     if (!response.ok) {
  //       throw new Error(`Roboflow API error: ${response.status}`);
  //     }

  //     // Parse the response
  //     const data = await response.json();
  //     console.log("vision response", data)
  //     setGeminiFoundDetections(data.boundingBoxes)

  //     if (!data || !data.boundingBoxes) {
  //       console.warn("No bounding boxes found in Vision response.");
  //       return { boundingBoxes: [], scaleFactor: 1 }; // Default empty response
  //     }

  //     // Return boundingBoxes and scaleFactor
  //     return data;
  //   } catch (error) {
  //     console.error("Error triggering Vision detections:", error);
  //     return { boundingBoxes: [], scaleFactor: 1 }; // Return defaults on error
  //   }
  // };

  async function triggerRoboflowDetections(imageUrl) {

    try {
      const response = await fetch('/.netlify/functions/roboflowDetections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch detections');
      }

      const result = await response.json();
      console.log("🔍 RoboFlow result:", result);
      console.log("🔍 Raw predictions array:", result.predictions);

      if (!result.predictions || !Array.isArray(result.predictions)) {
        console.error('Invalid API response format:', result);
        return [];
      }

      // ✅ Process predictions using polygon points for automatic perspective
      const processedPredictions = result.predictions
        .filter(prediction => {
          console.log(`🔍 FacadeDesigner checking: class="${prediction.class}", confidence=${prediction.confidence}`);
          // New model uses capitalized class names: Door, Window
          const isValidType = prediction.class === 'Door' || prediction.class === 'Window';
          const hasValidConfidence = prediction.confidence > 0.88; // Balanced to catch doors (~88%) and full windows (>90%)
          return isValidType && hasValidConfidence;
        })
        .map(prediction => {
          // Normalize class name to lowercase
          const normalizedClass = prediction.class.toLowerCase();
          
          // ✅ Extract 4 corner points from polygon for perspective
          let perspectivePoints = null;
          let top, left, bottom, right;
          
          if (prediction.points && Array.isArray(prediction.points) && prediction.points.length >= 4) {
            const points = prediction.points;
            
            const xCoords = points.map(p => p.x);
            const yCoords = points.map(p => p.y);
            
            left = Math.min(...xCoords);
            right = Math.max(...xCoords);
            top = Math.min(...yCoords);
            bottom = Math.max(...yCoords);
            
            // Find points closest to each corner
            const topLeft = points.reduce((closest, p) => {
              const dist = Math.sqrt(Math.pow(p.x - left, 2) + Math.pow(p.y - top, 2));
              const closestDist = Math.sqrt(Math.pow(closest.x - left, 2) + Math.pow(closest.y - top, 2));
              return dist < closestDist ? p : closest;
            });
            
            const topRight = points.reduce((closest, p) => {
              const dist = Math.sqrt(Math.pow(p.x - right, 2) + Math.pow(p.y - top, 2));
              const closestDist = Math.sqrt(Math.pow(closest.x - right, 2) + Math.pow(closest.y - top, 2));
              return dist < closestDist ? p : closest;
            });
            
            const bottomRight = points.reduce((closest, p) => {
              const dist = Math.sqrt(Math.pow(p.x - right, 2) + Math.pow(p.y - bottom, 2));
              const closestDist = Math.sqrt(Math.pow(closest.x - right, 2) + Math.pow(closest.y - bottom, 2));
              return dist < closestDist ? p : closest;
            });
            
            const bottomLeft = points.reduce((closest, p) => {
              const dist = Math.sqrt(Math.pow(p.x - left, 2) + Math.pow(p.y - bottom, 2));
              const closestDist = Math.sqrt(Math.pow(closest.x - left, 2) + Math.pow(closest.y - bottom, 2));
              return dist < closestDist ? p : closest;
            });
            
            perspectivePoints = [topLeft, topRight, bottomRight, bottomLeft];
            
            console.log(`📐 Using polygon corners for ${normalizedClass}:`, perspectivePoints);
          } else {
            top = prediction.y - prediction.height / 2;
            left = prediction.x - prediction.width / 2;
            bottom = prediction.y + prediction.height / 2;
            right = prediction.x + prediction.width / 2;
          }
          
          return {
            class: normalizedClass,
            confidence: prediction.confidence,
            tlbr: [top, left, bottom, right],
            detection_id: prediction.detection_id,
            hasPolygon: !!(prediction.points && prediction.points.length > 0),
            perspectiveCorners: perspectivePoints
          };
        });

        // ✅ Filter overlapping detections - keep only largest items in same area
        const filteredPredictions = processedPredictions.filter((pred, index, arr) => {
          const [top1, left1, bottom1, right1] = pred.tlbr;
          const area1 = (right1 - left1) * (bottom1 - top1);
          
          // Check if this prediction overlaps with any larger prediction
          for (let i = 0; i < arr.length; i++) {
            if (i === index) continue; // Skip self
            
            const other = arr[i];
            const [top2, left2, bottom2, right2] = other.tlbr;
            const area2 = (right2 - left2) * (bottom2 - top2);
            
            // Calculate intersection
            const intersectLeft = Math.max(left1, left2);
            const intersectTop = Math.max(top1, top2);
            const intersectRight = Math.min(right1, right2);
            const intersectBottom = Math.min(bottom1, bottom2);
            
            if (intersectLeft < intersectRight && intersectTop < intersectBottom) {
              // There is overlap
              const intersectArea = (intersectRight - intersectLeft) * (intersectBottom - intersectTop);
              const overlapRatio = intersectArea / Math.min(area1, area2);
              
              // If >50% overlap and the other is larger, filter this one out
              if (overlapRatio > 0.5 && area2 > area1) {
                console.log(`🗑️ Filtering overlapping ${pred.class}: area=${area1.toFixed(0)}, overlapped by larger ${other.class}: area=${area2.toFixed(0)}, overlap=${(overlapRatio*100).toFixed(1)}%`);
                return false;
              }
            }
          }
          
          return true; // Keep this prediction
        });

      console.log(`✅ Processed predictions: ${processedPredictions.length} → ${filteredPredictions.length} after overlap filtering`);
      return filteredPredictions;
    } catch (error) {
      console.error('Error triggering RoboFlow detections:', error);
      return [];
    }
  }








  const uploadImageToOpenAI = async (imageUrl) => {
    try {
      // Extract the actual URL if imageUrl is a Fabric.js object
      let actualImageUrl = imageUrl;
      if (typeof imageUrl === 'object' && imageUrl.src) {
        actualImageUrl = imageUrl.src;
      } else if (typeof imageUrl === 'string') {
        actualImageUrl = imageUrl;
      } else {
        throw new Error('Invalid imageUrl format - must be a string URL or Fabric object with src property');
      }

      const response = await fetch('/.netlify/functions/openAIImageUpload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: actualImageUrl }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Extract and validate the file_id
      const fileId = data.file_id;
      if (!fileId) {
        throw new Error('No file_id field found in the OpenAI response.');
      }

      return fileId; // Return only the file_id
    } catch (error) {
      console.error('Error uploading image to OpenAI:', error.message);
      throw error;
    }
  };




  function parseBoundingBoxes(boundingBoxesString) {
    const lines = boundingBoxesString.split("\n").filter(line => line.trim() && !line.startsWith("Here are"));

    return lines.map(line => {
      const parts = line.trim().split(" ");
      const [top, left, bottom, right] = parts.slice(1, 5).map(Number);
      const boxClass = parts[5]; // "window" or "door"

      return {
        class: boxClass,
        tlbr: [top, left, bottom, right],
      };
    });
  }

  // ✅ Enhanced function to save conversation data with fileId and immediate Firebase sync
  const saveProjectConversationData = async (threadId, runId, conversationHistory, fileId = null) => {
    // ✅ Skip saving for guest users
    const isGuestUser = currentUser && currentUser.email.startsWith("guest_");
    if (isGuestUser) {
      console.log("🚫 Skipping conversation data save for guest user");
      return;
    }

    if (!projectId) return;
    
    if (isProjectSwitching) {
      console.log("🚫 Skipping save - project switching in progress");
      return;
    }
    
    if (isSaving) {
      console.log("⏳ Save operation already in progress, skipping...");
      return;
    }
    
    // � IMPROVED: More flexible validation - save if we have threadId OR meaningful conversation data
    if (!threadId && (!conversationHistory || conversationHistory.length === 0)) {
      console.log("🚫 Skipping save - no valid conversation data:", {
        hasThreadId: !!threadId,
        hasConversationHistory: !!(conversationHistory && conversationHistory.length > 0)
      });
      return;
    }
    
    setIsSaving(true);
    
    try {
      // 🔍 VALIDATION: Check if we're trying to save contaminated data
      const projectRef = doc(db, "Projects", projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        const mainProjectThreadId = projectData.threadId;
        
        // If main project has a different threadId than what we're trying to save, it's contamination
        if (mainProjectThreadId && threadId && mainProjectThreadId !== threadId) {
          // Use the main project threadId instead of the contaminated one
          threadId = mainProjectThreadId;
        }
      }
      
      try {
        // ✅ Use updateDoc to safely merge conversation data without overwriting
        const updatePayload = {
          "conversationData.lastUpdated": new Date().toISOString(),
          "conversationData.projectId": projectId  // ✅ TAG conversation with project ID
        };

        // Also save threadId at the main project level for cleanup logic
        if (threadId) {
          updatePayload["conversationData.threadId"] = threadId;
          updatePayload["threadId"] = threadId;
        }

        if (runId) {
          updatePayload["conversationData.runId"] = runId;
        }

        // Include fileId if provided
        if (fileId) {
          updatePayload["conversationData.fileId"] = fileId;
        }

        if (conversationHistory !== undefined) {
          updatePayload["conversationData.conversationHistory"] = conversationHistory;
        }

        await updateDoc(projectRef, updatePayload);

        // 🔄 Immediately refresh conversation data from Firebase to ensure sync
        await loadConversationDataFromFirebase();
        
      } catch (updateError) {
        console.error("❌ Error updating project conversation data:", updateError);
        
        // Fallback: if document doesn't exist, create it with setDoc
        if (updateError.code === 'not-found') {
          try {
            const conversationDataToCreate = {
              lastUpdated: new Date().toISOString()
            };

            if (threadId) {
              conversationDataToCreate.threadId = threadId;
            }

            if (runId) {
              conversationDataToCreate.runId = runId;
            }

            if (fileId) {
              conversationDataToCreate.fileId = fileId;
            }

            if (conversationHistory !== undefined) {
              conversationDataToCreate.conversationHistory = conversationHistory;
            } else {
              conversationDataToCreate.conversationHistory = [];
            }

            await setDoc(projectRef, { 
              conversationData: conversationDataToCreate
            }, { merge: true });
            
            console.log("✅ Created new project with conversation data:", projectId);
            
            // 🔄 Immediately refresh conversation data from Firebase to ensure sync
            await loadConversationDataFromFirebase();
            
          } catch (setError) {
            console.error("❌ Error creating project document:", setError);
          }
        }
      }
      
    } finally {
      setIsSaving(false);
    }
  };

  // ✅ New function to load conversation data from Firebase and sync with local state
  const loadConversationDataFromFirebase = async () => {
    if (!projectId) {
      console.log("⚠️ No projectId available - cannot load conversation data");
      return null;
    }

    try {
      const projectRef = doc(db, "Projects", projectId);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        
        // Extract conversation data
        let conversationData = null;
        
        if (projectData.conversationData) {
          // New nested format
          conversationData = projectData.conversationData;
        } else if (projectData.threadId || projectData.fileId) {
          // Old flat format
          conversationData = {
            threadId: projectData.threadId,
            runId: projectData.runId,
            fileId: projectData.fileId,
            conversationHistory: projectData.conversationHistory || []
          };
          console.log("📥 Loaded conversation data from OLD flat format:", conversationData);
        }
        
        // 🔄 Update local state with Firebase data (this is the source of truth)
        if (conversationData) {
          if (conversationData.threadId && conversationData.threadId !== threadId) {
            setThreadId(conversationData.threadId);
            
            // Also update main project level threadId if it doesn't match
            if (projectData.threadId !== conversationData.threadId) {
              const syncPayload = { "threadId": conversationData.threadId };
              await updateDoc(projectRef, syncPayload).catch(error => {
                console.warn("⚠️ Could not sync threadId to main project level:", error);
              });
            }
          }
          
          if (conversationData.runId && conversationData.runId !== runId) {
            setRunId(conversationData.runId);
          }
          
          if (conversationData.fileId && conversationData.fileId !== imageFileId) {
            setImageFileId(conversationData.fileId);
          }
          
          if (conversationData.conversationHistory) {
            const firebaseHistoryLength = conversationData.conversationHistory.length;
            const currentHistoryLength = conversationHistory.length;
            
            if (firebaseHistoryLength !== currentHistoryLength) {
              setConversationHistory(conversationData.conversationHistory);
            }
          }
        }
        
        return conversationData;
      } else {
        console.log("📭 No project document found in Firebase for:", projectId);
        return null;
      }
    } catch (error) {
      console.error("❌ Error loading conversation data from Firebase:", error);
      return null;
    }
  };

  // ✅ Cleanup function to reset contaminated conversation data
  const cleanupContaminatedConversationData = async () => {
    if (!projectId) {
      console.log("❌ No projectId available for cleanup");
      return false;
    }

    try {
      const projectRef = doc(db, "Projects", projectId);
      const docSnap = await getDoc(projectRef);
      
      if (docSnap.exists()) {
        const projectData = docSnap.data();
        // Check if conversation data threadId differs from main project threadId
        if (projectData.conversationData && projectData.conversationData.threadId) {
          if (projectData.threadId && projectData.conversationData.threadId !== projectData.threadId) {
            
            // ✅ Skip Firebase cleanup for guest users
            const isGuestUser = currentUser && currentUser.email.startsWith("guest_");
            if (!isGuestUser) {
              // Reset conversation data to use main project threadId and clear history
              await updateDoc(projectRef, { 
                "conversationData.threadId": projectData.threadId,
                "conversationData.runId": projectData.runId || null,
                "conversationData.conversationHistory": [], // Clear contaminated history
                "conversationData.lastUpdated": new Date().toISOString(),
                "conversationData.cleanupPerformed": true
              });
            } else {
              console.log("🚫 Skipping Firebase cleanup for guest user");
            }
            
            console.log("✅ Conversation data cleanup completed for project:", projectId);
            return true; // Indicates cleanup was performed
          } else if (!projectData.threadId && projectData.conversationData.threadId) {
            // Main project has no threadId but conversation has one - this is contamination
            console.log("⚠️ Main project has no threadId but conversation has:", projectData.conversationData.threadId);
            console.log("   This suggests conversation data may be contaminated from another project");
            console.log("   Syncing conversation threadId to main project level...");
            
            // ✅ Skip Firebase sync for guest users
            const isGuestUser = currentUser && currentUser.email.startsWith("guest_");
            if (!isGuestUser) {
              // Instead of clearing, sync the conversation threadId to main project level
              await updateDoc(projectRef, { 
                "threadId": projectData.conversationData.threadId,
                "conversationData.lastUpdated": new Date().toISOString(),
                "conversationData.syncPerformed": true
              });
            } else {
              console.log("🚫 Skipping Firebase sync for guest user");
            }
            
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    } catch (error) {
      console.error("❌ Error during conversation cleanup for project", projectId + ":", error);
      return false;
    }
  };

  // Debug function to log current project's conversation data from Firebase
  const logCurrentProjectConversationData = async () => {
    if (!projectId) {
      console.log("No project loaded - cannot log conversation data");
      return;
    }

    try {
      const projectRef = doc(db, "Projects", projectId);
      const docSnap = await getDoc(projectRef);
      
      if (docSnap.exists()) {
        const projectData = docSnap.data();
        
        // Check for new nested conversation data structure
        if (projectData.conversationData) {
        } 
        // Check for old flat structure (threadId, fileId, etc. at root level)
        else if (projectData.threadId || projectData.fileId) {
        }
      } else {
      }
    } catch (error) {
      console.error("❌ Error fetching project conversation data:", error);
    }
  };

  const startConversation = async (userInput, fileId, requestMode = 'ideas') => {
    try {
      setStatusMessage(t('assistant.statusAnalyzing'));
      setIsLoading(true);

      // ✅ Get imageUrl from backgroundImage or fabricCanvas
      const canvasBackgroundImage = fabricCanvas?.backgroundImage;
      const imageUrlFromCanvas = canvasBackgroundImage?.getSrc ? canvasBackgroundImage.getSrc() : canvasBackgroundImage?.src;
      const projectImageUrl = backgroundImage || imageUrlFromCanvas;
      
      if (!projectImageUrl) {
        throw new Error('No project image available. Please ensure the project has a background image.');
      }
      
      // Get language from i18n
      const language = i18n.language || 'en'; // Default to 'en' if not set
      
      // ✅ Track all progress messages
      let allProgressMessages = [];
      
      // Keep temporary progress in the workflow modal, outside saved chat history.
      const buildProgressHtml = (message) => `<div style="color: #1976d2; padding: 8px 12px; background: #e3f2fd; border-radius: 4px; margin: 4px 0; font-size: 14px; display: flex; align-items: center;">
          <div class="spinner-border spinner-border-sm" role="status" style="margin-right: 8px; width: 14px; height: 14px; border-width: 2px;"></div>${message}
        </div>`;

      const addProgressMessage = (message) => {
        // Replace spinner in previous messages with checkmark
        allProgressMessages = allProgressMessages.map(msg => 
          msg.replace(/<div class="spinner-border spinner-border-sm" role="status" style="margin-right: 8px; width: 14px; height: 14px; border-width: 2px;"><\/div>/, '<span style="margin-right: 8px;">✅</span>')
        );
        
        // Add new message with animated spinner
        const progressHtml = buildProgressHtml(message);
        
        allProgressMessages.push(progressHtml);
        
        setWorkflowProgress(allProgressMessages.join(''));
      };
      
      // Initialize the modal progress for this request.
      // Just track it in allProgressMessages array
      const firstMessage = requestMode === 'show'
        ? t('assistant.progressAnalyzingImage', { defaultValue: '🔍 Analyzing image...' })
        : t('assistant.progressReviewingRequest', { defaultValue: '💡 Reviewing your facade and request...' });
      const firstProgressHtml = buildProgressHtml(firstMessage);
      allProgressMessages.push(firstProgressHtml);
      setWorkflowProgress(firstProgressHtml);
      
      // Add delay to ensure sequential rendering of next messages
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // ✅ Process roboflow detections and prepare structured data for GPT-4o
      const currentDetections = window.roboflowDetectionsForGPT || [];
      let detectionData = null;
      
      if (requestMode === 'show' && currentDetections && currentDetections.length > 0) {
        const sortedDetections = filterAndSortDetections(currentDetections);
        
        // Number doors and windows separately, left-to-right
        let doorIndex = 0;
        let windowIndex = 0;
        const doors = [];
        const windows = [];
        
        sortedDetections.forEach(detection => {
          const coords = {
            top: Math.round(detection.tlbr[0]),
            left: Math.round(detection.tlbr[1]),
            bottom: Math.round(detection.tlbr[2]),
            right: Math.round(detection.tlbr[3])
          };
          
          if (detection.class === "door") {
            doorIndex++;
            doors.push({ index: doorIndex, coords });
          } else if (detection.class === "window") {
            windowIndex++;
            windows.push({ index: windowIndex, coords });
          }
        });
        
        detectionData = { doors, windows };
        console.log("📍 Sending detection coordinates to GPT-4o:", detectionData);
        
        // Show detection counts in chat
        const doorCount = doors.length;
        const windowCount = windows.length;
        const detectionMsg = t('assistant.progressFilteredDetections', {
          defaultValue: '✅ Filtered detections: {{doorCount}} door(s), {{windowCount}} window(s)',
          doorCount,
          windowCount
        });
        addProgressMessage(detectionMsg);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // ✅ Start background job for OpenAI Vision Chat
      addProgressMessage(
        requestMode === 'show'
          ? t('assistant.progressSendingForClassification', { defaultValue: '🤖 Sending to AI for classification...' })
          : t('assistant.progressSendingForRecommendations', { defaultValue: '🤖 Sending to AI for recommendations...' })
      );
      await new Promise(resolve => setTimeout(resolve, 300));
      console.log("🚀 Starting background job for OpenAI Vision analysis...");
      
      const startResponse = await fetch('/.netlify/functions/openAIVisionChat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userInput, 
          imageUrl: projectImageUrl,
          language,
          detections: detectionData,
          background: true, // Enable background processing with -background function
          mode: requestMode
        }),
      });

      if (!startResponse.ok) {
        throw new Error(`HTTP error! status: ${startResponse.status}`);
      }

      const { jobId } = await startResponse.json();
      console.log("✅ Background job started:", jobId);
      addProgressMessage(t('assistant.progressProcessing', { defaultValue: '⏳ Processing (20-30 seconds)...' }));
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Poll for job completion
      const pollInterval = 2000; // Poll every 2 seconds
      const maxAttempts = 60; // Max 2 minutes
      let attempts = 0;
      
      const pollJobStatus = async () => {
        attempts++;
        
        if (attempts > maxAttempts) {
          throw new Error('Job timed out after 2 minutes');
        }
        
        // Update progress every 10 seconds
        if (attempts % 5 === 0) {
          const elapsed = attempts * 2;
          addProgressMessage(t('assistant.progressStillProcessing', {
            defaultValue: '⏳ Still processing... ({{elapsed}}s)',
            elapsed
          }));
        }
        
        const statusResponse = await fetch('/.netlify/functions/openAIVisionChatStatus', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobId }),
        });
        
        if (!statusResponse.ok) {
          throw new Error(`Status check failed: ${statusResponse.status}`);
        }
        
        const jobData = await statusResponse.json();
        console.log(`🔄 Job status (attempt ${attempts}):`, jobData.status);
        
        if (jobData.status === 'completed') {
          console.log("✅ Job completed successfully");
          addProgressMessage(t('assistant.progressAnalysisComplete', { defaultValue: '✅ Analysis complete!' }));
          return jobData.result;
        } else if (jobData.status === 'error') {
          throw new Error(jobData.error || 'Job failed');
        }
        
        // Still processing, wait and poll again
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        return pollJobStatus();
      };
      
      // Wait for job to complete
      const data = await pollJobStatus();
      
      // ✅ Check for error responses from backend
      if (data.error) {
        const errorResponse = `<div style="color: #d32f2f; padding: 10px; background: #ffebee; border-radius: 4px;">
          <strong>${t('assistant.errorLabel', { defaultValue: 'Error' })}:</strong> ${data.message || t('assistant.processingError', { defaultValue: 'An error occurred while processing your request.' })}
        </div>`;
        
        setConversationHistory(prev => {
          const updated = [...prev];
          const pendingIndex = updated.findIndex(conv =>
            conv.response === null ||
            conv.response?.includes('color: #1976d2') ||
            conv.response?.includes('spinner-border')
          );
          if (pendingIndex !== -1) {
            updated[pendingIndex] = { ...updated[pendingIndex], response: errorResponse };
          }
          return updated;
        });
        
        setStatusMessage(t('assistant.statusError'));
        setIsLoading(false);
        return data.message;
      }
      
      // ✅ Store door and window types
      let doorTypesSummary = '';
      let windowTypesSummary = '';
      
      if (data.doorTypes && Array.isArray(data.doorTypes)) {
        console.log("🚪 Received door types from AI:", data.doorTypes);
        window.detectedDoorTypes = data.doorTypes;
        doorTypesSummary = t('assistant.progressDoorClassified', {
          defaultValue: '🚪 {{count}} door(s) classified',
          count: data.doorTypes.length
        });
      }
      
      if (data.windowTypes && Array.isArray(data.windowTypes)) {
        console.log("🪟 Received window types from AI:", data.windowTypes);
        window.detectedWindowTypes = data.windowTypes;
        windowTypesSummary = t('assistant.progressWindowClassified', {
          defaultValue: '🪟 {{count}} window(s) classified',
          count: data.windowTypes.length
        });
      }
      
      // Add classification summary
      if (doorTypesSummary || windowTypesSummary) {
        const summaryMsg = [doorTypesSummary, windowTypesSummary].filter(Boolean).join(', ');
        addProgressMessage(`✅ ${summaryMsg}`);
      }
      
      // Small delay to show final progress message
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // ✅ Clean HTML/markdown artifacts
      let responseText = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
      responseText = responseText
        .replace(/^```html\s*/g, '')
        .replace(/\s*```$/g, '')
        .replace(/```json\s*/g, '')
        .replace(/```\s*/g, '')
        .replace(/<h3>\s*Detected Door Types\s*<\/h3>\s*<p>[\s\S]*?<\/p>/gi, '')
        .replace(/<h3>\s*Types de Portes Détectées\s*<\/h3>\s*<p>[\s\S]*?<\/p>/gi, '')
        .replace(/<h3>\s*Detected Window Types\s*<\/h3>\s*<p>[\s\S]*?<\/p>/gi, '')
        .replace(/<h3>\s*Types de Fenêtres Détectées\s*<\/h3>\s*<p>[\s\S]*?<\/p>/gi, '')
        .trim();
      
      // ✅ Replace progress messages with final response
      flushSync(() => {
        setConversationHistory(prev => {
          const updated = [...prev];
          const pendingIndex = updated.findIndex(conv => 
            conv.response === null || 
            conv.response?.includes('color: #1976d2') ||
            conv.response?.includes('spinner-border')
          );
          if (pendingIndex !== -1) {
            updated[pendingIndex] = { ...updated[pendingIndex], response: responseText };
            
            // ✅ Save updated conversation history to Firebase
            saveProjectConversationData(null, null, updated, fileId).catch(error => 
              console.error("Error saving conversation history:", error)
            );
          }
          return updated;
        });
      });
      
      // ✅ Process the response to extract colors and create buttons
      if (requestMode === 'show') {
        processAssistantResponse(responseText);
      } else {
        setColorButtons([]);
        setProposedColors({});
      }
      
      // ✅ Scroll to show the latest conversation after response is added
      setTimeout(() => {
        if (responseContainerRef.current) {
          responseContainerRef.current.scrollTop = responseContainerRef.current.scrollHeight;
        }
      }, 100);
      
      setStatusMessage(t('assistant.statusComplete'));
      handleAnalyticsEvent('DesignerResponse', 'Designer', 'Designer Responded');
      setIsLoading(false);
      return responseText;
    } catch (error) {
      console.error('Error starting conversation:', error);
      
      // ✅ Update the pending conversation entry with error message
      setConversationHistory(prev => {
        const updated = [...prev];
        const pendingIndex = updated.findIndex(conv =>
          conv.response === null ||
          conv.response?.includes('color: #1976d2') ||
          conv.response?.includes('spinner-border')
        );
        if (pendingIndex !== -1) {
          const errorResponse = `<div style="color: #d32f2f; padding: 10px; background: #ffebee; border-radius: 4px;">
            <strong>${t('assistant.errorLabel', { defaultValue: 'Error' })}:</strong> ${error.message || t('assistant.processingErrorRetry', { defaultValue: 'An error occurred while processing your request. Please try again.' })}
          </div>`;
          updated[pendingIndex] = { ...updated[pendingIndex], response: errorResponse };
        }
        return updated;
      });
      
      setStatusMessage(t('assistant.statusError'));
      setIsLoading(false);
      throw error;
    }
  };

  // ✅ Polling function for background processing
  const pollForResult = async (threadId, runId, toastId, requestMode = 'ideas') => {
    const maxPollAttempts = 60; // Poll for up to 2 minutes
    let pollCount = 0;
    
    while (pollCount < maxPollAttempts) {
      try {
        const response = await fetch('/.netlify/functions/checkAssistantInteraction', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ threadId, runId }),
        });

        const data = await response.json();
        console.log(`Poll attempt ${pollCount + 1}:`, data);
        
        if (response.ok && data.status === 'completed' && data.message) {
          // ✅ Got the result
          setConversationHistory(prev => {
            const updated = [...prev];
            const pendingIndex = updated.findIndex(conv =>
              conv.response === null ||
              conv.response?.includes('color: #1976d2') ||
              conv.response?.includes('spinner-border')
            );
            if (pendingIndex !== -1) {
              updated[pendingIndex] = { ...updated[pendingIndex], response: data.message };
              console.log("Updated pending conversation with result:", updated[pendingIndex]);
              
              // ✅ Save updated conversation history to Firebase with fileId
              saveProjectConversationData(threadId, runId, updated, imageFileId).catch(error => 
                console.error("Error saving conversation history:", error)
              );
            }
            return updated;
          });
          
          // ✅ Process the response to extract colors and create buttons
          if (requestMode === 'show') {
            processAssistantResponse(data.message);
          } else {
            setColorButtons([]);
            setProposedColors({});
          }
          
          // ✅ Scroll to show the latest conversation after polling response
          setTimeout(() => {
            if (responseContainerRef.current) {
              responseContainerRef.current.scrollTop = responseContainerRef.current.scrollHeight; // Scroll to bottom (latest conversation)
              console.log("Scrolled to bottom after polling response");
            }
          }, 100);
          
          toast.dismiss(toastId);
          toast.success(t('assistant.toastSuccessVision'));
          setStatusMessage(t('assistant.statusCompleted'));
          setIsLoading(false);
          
          return;
        } else if (data.status === 'in_progress' || data.status === 'queued') {
          console.log(`Poll attempt ${pollCount + 1}: Status is ${data.status}, continuing to poll...`);
          pollCount++;
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before next poll
        } else if (data.status === 'failed') {
          throw new Error(data.error || 'Assistant run failed');
        } else {
          console.log(`Poll attempt ${pollCount + 1}: Unexpected response:`, data);
          pollCount++;
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error('Error polling for result:', error);
        pollCount++;
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    // If we get here, polling timed out
    setConversationHistory(prev => {
      const updated = [...prev];
      const pendingIndex = updated.findIndex(conv =>
        conv.response === null ||
        conv.response?.includes('color: #1976d2') ||
        conv.response?.includes('spinner-border')
      );
      if (pendingIndex !== -1) {
        updated[pendingIndex] = {
          ...updated[pendingIndex],
          response: `<div style="color: #d32f2f; padding: 10px; background: #ffebee; border-radius: 4px;">
            <strong>${t('assistant.errorLabel', { defaultValue: 'Error' })}:</strong> ${t('assistant.processingTimedOut', { defaultValue: 'Processing timed out. Please try again.' })}
          </div>`
        };
      }
      return updated;
    });

    throw new Error('Background processing timed out');
  };

  // ✅ Function to completely clear conversation state (used during project switching)
  const clearConversationState = () => {
    setConversationHistory([]);
    setThreadId(null);
    setRunId(null);
    setImageFileId(null);
    setAssistantResponse('');
    // ✅ DON'T clear userInput - let user keep typing
    // setUserInput(''); 
    setProposedColors({});
    setColorButtons([]);
    setErrorMessage('');
    setStatusMessage('');
    setIsLoading(false);
    // ✅ Reset first interaction flag for new projects
    setIsFirstInteraction(true);
  };

  // ✅ Function to initialize a new project with fresh state
  const initializeNewProject = async (projectId) => {
    try {
      // Get current background image from canvas or props
      const currentImageUrl = backgroundImage?.src || backgroundImage;
      
      if (currentImageUrl) {
        console.log("📸 Uploading new project image to OpenAI...");
        
        // Upload image to OpenAI and get fileId
        const fileId = await uploadImageToOpenAI(currentImageUrl);
        
        if (fileId) {
          console.log("✅ New project initialized with fileId:", fileId);
          setImageFileId(fileId);
          
          // ✅ Save initial project state to Firebase with fileId (skip for guests)
          const isGuestUser = currentUser && currentUser.email.startsWith("guest_");
          if (!isGuestUser) {
            try {
              const projectRef = doc(db, "Projects", projectId);
              await updateDoc(projectRef, {
                fileId: fileId,
                initializationTimestamp: new Date().toISOString()
              });
              console.log("✅ New project fileId saved to Firebase");
            } catch (error) {
              console.error("❌ Error saving new project fileId to Firebase:", error);
            }
          } else {
            console.log("🚫 Skipping fileId save to Firebase for guest user");
          }
        }
      } else {
        // No background image available
      }
    } catch (error) {
      console.error("❌ Error initializing new project:", error);
    }
  };

  // ✅ Function to clear everything including user input (only when starting completely fresh)
  const clearCompletelyIncludingInput = () => {
    clearConversationState();
    setUserInput('');
  };

  // ✅ Function to clean contaminated conversation data from Firebase
  const cleanContaminatedConversationData = async (projectId, correctThreadId = null) => {
    if (!projectId) return;
    
    // ✅ Skip Firebase cleaning for guest users
    const isGuestUser = currentUser && currentUser.email.startsWith("guest_");
    if (isGuestUser) {
      console.log("🚫 Skipping Firebase conversation data cleaning for guest user");
      return;
    }
    
    try {
      console.log(`🧹 Cleaning contaminated conversation data for project: ${projectId}`);
      const projectRef = doc(db, "Projects", projectId);
      
      const updatePayload = {
        "conversationData": deleteField() // Remove contaminated conversation data completely
      };
      
      // If there was no thread for this project originally, also clear the main threadId
      if (!correctThreadId) {
        updatePayload["threadId"] = deleteField();
      }
      
      await updateDoc(projectRef, updatePayload);
      console.log(`✅ Cleaned contaminated conversation data from project: ${projectId}`);
    } catch (error) {
      console.error(`❌ Error cleaning contaminated data for project ${projectId}:`, error);
    }
  };

  const handleSubmit = async () => {

    if (isLoading || isFacadeAIGenerating) return;

    // Reset the error message on submit
    setErrorMessage(""); 

    // Check if the input is empty and show the error message if true
    if (!userInput) {
      setErrorMessage(t("assistant.errorMessage"));
      return;
    }
    
    // 🚫 CRITICAL: Don't process if we're switching projects or have no valid project
    if (isProjectSwitching || !projectId) {
      console.log("🚫 Skipping submit - project switching or no project ID");
      return;
    }

    const requestMode = activeDesignerMode === 'show' ? 'show' : 'ideas';

    const interactionStatus = getDesignerInteractionUsageStatus();
    if (!interactionStatus.canInteract) {
      toast.error(
        t('assistant.interactionLimitReached', {
          count: interactionStatus.blockedCount,
          limit: interactionStatus.blockedLimit,
          period: t(`assistant.${interactionStatus.blockedPeriod}`, {
            defaultValue: interactionStatus.blockedPeriod,
          }),
          defaultValue: `Interaction limit reached (${interactionStatus.blockedCount}/${interactionStatus.blockedLimit} ${interactionStatus.blockedPeriod}).`,
        })
      );
      return;
    }

    setWorkflowMode(requestMode);
    setWorkflowProgress('');
    setWorkflowModalOpen(true);
    if (isDesignerModalOpen) {
      updateAppState({ aiDesignerVisible: false, aiShowModeSelected: false });
      setDesignerModalOpen(false);
    }

    // Save the question while displaying progress only in the workflow modal.
    const currentQuestion = userInput;
    const firstProgressText = requestMode === 'show'
      ? t('assistant.progressAnalyzingImage', { defaultValue: '🔍 Analyzing image...' })
      : t('assistant.progressReviewingRequest', { defaultValue: '💡 Reviewing your facade and request...' });
    const firstProgressMessage = `<div style="color: #1976d2; padding: 8px 12px; background: #e3f2fd; border-radius: 4px; margin: 4px 0; font-size: 14px; display: flex; align-items: center;">
          <div class="spinner-border spinner-border-sm" role="status" style="margin-right: 8px; width: 14px; height: 14px; border-width: 2px;"></div>${firstProgressText}
        </div>`;
    
    setWorkflowProgress(firstProgressMessage);
    setConversationHistory(prev => {
      const questionEntry = { question: currentQuestion, response: null };
      const newHistory = [questionEntry, ...prev];
      
      // ✅ Save question to Firebase immediately, but only if we have a thread
      // If no thread yet, it will be saved when the thread is created in startConversation
      if (threadId) {
        saveProjectConversationData(threadId, runId, newHistory, imageFileId).catch(error => 
          console.error("Error saving question to conversation history:", error)
        );
      } else {
        // No threadId yet, question will be saved when thread is created
      }
      
      return newHistory;
    });

    // ✅ Clear input immediately after capturing
    setUserInput('');

    // ✅ Scroll to show the latest conversation immediately
    setTimeout(() => {
      if (responseContainerRef.current) {
        responseContainerRef.current.scrollTop = responseContainerRef.current.scrollHeight; // Scroll to bottom (latest conversation)
      }
    }, 100);

    // Move directly into AI waiting when the user sends from the Show Me guidance step
    if (isStepActive('show-mode-selected')) {
      completeStep('show-mode-selected');
      completeStep('submit-request');
      updateAppState({ aiLoading: true });
      console.log('🎯 OnboardingDebug: Show Me mode confirmed and request submitted, moving to processing-request step');
    } else if (isStepActive('submit-request')) {
      completeStep('submit-request');
      updateAppState({ aiLoading: true });
      console.log('🎯 OnboardingDebug: User submitted question, moving to processing-request step');
    }

    setIsLoading(true);

    try {
      const backgroundImage = fabricCanvas.backgroundImage;
      const imageUrlFromCanvas = backgroundImage?.getSrc();
      
      // ✅ CRITICAL: Always use the current project's image, not cached fileId
      const chosenImageUrl = backgroundImageUrl || imageUrlFromCanvas;

      if (!chosenImageUrl) {
        throw new Error(t('assistant.noImage'));
      }

      const question = currentQuestion.trim() || defaultQuestion; // ✅ Use captured question

      let fileIdToUse = imageFileId || null;

      if (requestMode === 'show') {
        // ✅ For Show Me, keep the legacy upload + detection pipeline
        if (isFirstInteraction || !imageFileId) {
          fileIdToUse = await uploadImage(chosenImageUrl);

          if (!fileIdToUse) {
            throw new Error('Failed to upload image and retrieve file ID.');
          }

          setImageFileId(fileIdToUse); // Update state for future interactions
        } else {
          // ✅ For subsequent interactions in same project, reuse the fileId
          fileIdToUse = imageFileId;
          console.log("🔄 Reusing existing fileId for same project:", fileIdToUse);
        }
      }


      // Start the conversation with the valid file ID
      const responseMessage = await startConversation(question, fileIdToUse, requestMode);
      // ✅ processAssistantResponse is now called inside startConversation

      setStatusMessage(t('assistant.statusComplete'));
      setIsFirstInteraction(false);
      updateInteractionCounts();
    } catch (error) {
      console.error('Error in handleSubmit:', error.message);
      setAssistantResponse((prev) => `${prev}\n\n${t('assistant.errorAi')}`);
      setStatusMessage(t('assistant.statusError'));
      setConversationHistory(prev => prev.map((entry, index) =>
        index === 0 && entry.response === null
          ? { ...entry, response: t('assistant.errorNetwork') }
          : entry
      ));
      toast.error(t('assistant.errorNetwork'));
    } finally {
      setIsLoading(false);
    }
  };








  const handleProjectSwitch = (newImageUrl) => {


    if (!newImageUrl || newImageUrl === backgroundImageUrl) {
      // Even if no image change, mark switching as complete
      setIsProjectSwitching(false);
      return;
    }

    // ✅ Only clear UI states for visual changes, but preserve user's text input
    setImageFileId(null);
    setBackgroundImageUrl(newImageUrl); // Update the background image URL
    
    // ✅ DON'T clear userInput during project switching - let user keep typing
    // setUserInput(''); // REMOVED: Don't clear user's text
    
    setAssistantResponse('');
    setColorButtons([]);
    setProposedColors({});
    setIsFirstInteraction(true);
    setIsDetectionTriggered(false);
    // ✅ Don't clear conversation history here - it should be project-specific
    setStatusMessage('');
    
    // ✅ Mark project switching as complete to trigger conversation data loading
    setIsProjectSwitching(false);
    
    // ✅ Note: ThreadId/RunId and conversation history will be loaded by loadConversationData for the specific project
  };





  // ✅ Handle Designer modal close with conversation saving
  const handleDesignerClose = async () => {
    console.log("🔒 Designer closing - saving conversation data...");
    
    // Save current conversation data before closing
    if (projectId && threadId && conversationHistory.length > 0) {
      try {
        console.log("💾 Saving conversation data on Designer close:", {
          projectId,
          threadId,
          runId,
          conversationHistoryLength: conversationHistory.length
        });
        
        await saveProjectConversationData(threadId, runId, conversationHistory, imageFileId);
        console.log("✅ Conversation data saved on Designer close");
      } catch (error) {
        console.error("❌ Error saving conversation data on Designer close:", error);
      }
    } else {
      console.log("ℹ️ No conversation data to save on Designer close");
    }
    
    // Close the modal
    updateAppState({ aiDesignerVisible: false, aiShowModeSelected: false });
    setDesignerModalOpen(false);
  };

  const handleHintClick = (hintText) => {
    setUserInput(hintText);
    textAreaRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDeleteConversation = async (historyIndex) => {
    setConversationHistory((prev) => {
      if (historyIndex < 0 || historyIndex >= prev.length) {
        return prev;
      }

      const updatedHistory = prev.filter((_, index) => index !== historyIndex);

      if (expandedHistoryIndex !== null) {
        if (expandedHistoryIndex === historyIndex) {
          setExpandedHistoryIndex(null);
        } else if (expandedHistoryIndex > historyIndex) {
          setExpandedHistoryIndex(expandedHistoryIndex - 1);
        }
      }

      saveProjectConversationData(threadId, runId, updatedHistory, imageFileId).catch((error) =>
        console.error('Error deleting conversation entry:', error)
      );

      return updatedHistory;
    });
  };

  const startNewConversation = async () => {
    console.log("🧹 Clearing userInput in startNewConversation");
    setUserInput('');
    setAssistantResponse('');
    setIsLoading(false);
    // handleProjectSwitch('');
    resetNewProjectImage();
    setConversationHistory([])
    
    // ✅ Clear project-specific conversation data
    await saveProjectConversationData(null, null, []);
  };

  const updateInteractionCounts = async (threadId, runId) => {

    // console.log("environement", process.env.REACT_APP_ENV )

    const isGuestUser = currentUser && currentUser.email.includes('guest');

    if (isOnboardingActive) {
      return;
    }

    if (process.env.REACT_APP_ENV === 'development' && !isGuestUser) {
      // console.log("Skipping interaction count updates in development mode.");
      return;
    }

    if (isGuestUser) {
      if (isAdminGuestTestingSession) {
        setUserData((prevData) => ({
          ...prevData,
          weeklyInteractionCount: prevData?.weeklyInteractionCount || 0,
          assistantInteractionCount: prevData?.assistantInteractionCount || 0,
          planType: 'Admin',
        }));
        return;
      }

      // Retrieve interaction counts from local storage
      let weeklyInteractionCount = parseInt(localStorage.getItem('guest_weeklyInteractionCount')) || 0;
      let assistantInteractionCount = parseInt(localStorage.getItem('guest_assistantInteractionCount')) || 0;

      // Increment the counts
      weeklyInteractionCount += 1;
      assistantInteractionCount += 1;

      // Update local storage
      localStorage.setItem('guest_weeklyInteractionCount', weeklyInteractionCount);
      localStorage.setItem('guest_assistantInteractionCount', assistantInteractionCount);

      // console.log("Guest interaction counts updated in local storage:", { weeklyInteractionCount, assistantInteractionCount });

      // Update the local state to trigger a re-render
      setUserData((prevData) => ({
        ...prevData,
        weeklyInteractionCount,
        assistantInteractionCount
      }));

    } else if (currentUser && userRole !== 'mainAdmin' && userRole !== 'admin') {
      const userRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const weeklyInteractionCount = (userData.weeklyInteractionCount || 0) + 1;
        const assistantInteractionCount = (userData.assistantInteractionCount || 0) + 1;

        // console.log("Updating interaction counts in Firestore:", { weeklyInteractionCount, assistantInteractionCount, threadId, runId });

        const updateData = {
          weeklyInteractionCount,
          assistantInteractionCount,
        };

        if (threadId !== undefined) {
          updateData.threadId = threadId;
        }

        if (runId !== undefined) {
          updateData.runId = runId;
        }

        await updateDoc(userRef, updateData);

        // Update the local state to trigger a re-render
        setUserData((prevData) => ({
          ...prevData,
          weeklyInteractionCount,
          assistantInteractionCount
        }));
      }
    }
  };




  const convertTextUrlsToLinks = (text) => {
    const urlRegex = /www\.[\w\-\.]+(\.com|\.ca)/g;
    return text.replace(urlRegex, (url) => {
      return `<a href="http://${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
  };

  const extractBodyContent = (htmlString) => {
    // ✅ Comprehensive cleaning of markdown artifacts
    let cleanedString = htmlString
      .replace(/^```html\s*/g, '')     // Remove opening ```html
      .replace(/\s*```$/g, '')         // Remove closing ```
      .replace(/```json\s*/g, '')      // Remove ```json blocks
      .replace(/```\s*/g, '')          // Remove any remaining ``` blocks
      .trim();                         // Remove extra whitespace
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleanedString, 'text/html');
    return doc.body.innerHTML;
  };

  const createMarkup = (htmlContent) => {
    const content = typeof htmlContent === 'string' ? htmlContent : JSON.stringify(htmlContent);
    const cleanedContent = extractBodyContent(content);
    const processedContent = convertTextUrlsToLinks(cleanedContent);
    
    // ✅ Ensure all HTML links open in new tabs
    const linkProcessedContent = processedContent.replace(
      /<a\s+href="([^"]*)"(?![^>]*target=)/g, 
      '<a href="$1" target="_blank" rel="noopener noreferrer"'
    );
    
    return { __html: DOMPurify.sanitize(linkProcessedContent) };
  };


  const handleModalOpen = () => {
    hasOpenedDesignerDuringOnboardingRef.current = true;
    setDesignerModalOpen(true);
    handleAnalyticsEvent('click', 'Designer', 'Designer Triggered'); // Log event when modal is opened
    updateAppState({ aiDesignerVisible: true });
    
    // Complete onboarding step if active
    if (isStepActive('editor-loaded')) {
      completeStep('editor-loaded');
      
      // Log progression for debugging
      console.log('🎯 OnboardingDebug: Completed editor-loaded step, should move to ai-interaction');
    }
  };

  // Lock background scrolling when Designer modal is open, restore when closed
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isDesignerModalOpen) {
      try {
        prevBodyOverflowRef.current = document.body.style.overflow || '';
        document.body.style.overflow = 'hidden';
        // also prevent html from scrolling on some browsers
        document.documentElement.style.overflow = 'hidden';
      } catch (e) {
        // ignore
      }
    } else {
      try {
        document.body.style.overflow = prevBodyOverflowRef.current || '';
        document.documentElement.style.overflow = '';
        prevBodyOverflowRef.current = null;
      } catch (e) {
        // ignore
      }
    }

    // cleanup on unmount
    return () => {
      try {
        document.body.style.overflow = prevBodyOverflowRef.current || '';
        document.documentElement.style.overflow = '';
      } catch (e) {}
    };
  }, [isDesignerModalOpen]);

  const renderButtons = () => {
    if (!colorButtons.length || !colorButtons[0]) {
      // If colorButtons is empty or the first element is undefined, return null or a fallback button
      return null;
    }

    const formatPreviewLabel = (itemKey) => {
      if (!itemKey) return '';
      const normalizedKey = String(itemKey).trim().toLowerCase();
      if (normalizedKey === 'window' || normalizedKey === 'windows') {
        return t('assistant.windows', { defaultValue: 'Windows' });
      }
      if (normalizedKey === 'door' || normalizedKey === 'doors') {
        return t('assistant.doors', { defaultValue: 'Doors' });
      }
      if (normalizedKey === 'garagedoor' || normalizedKey === 'garage door') {
        return t('assistant.garageDoor', { defaultValue: 'Garage door' });
      }
      return normalizedKey.charAt(0).toUpperCase() + normalizedKey.slice(1);
    };

    const buttonText = colorButtons[0];
    const isShowMeButton = buttonText.toLowerCase() === 'show me';
    const previewEntries = Object.entries(proposedColorsRef.current || {}).filter(
      ([, colors]) => Array.isArray(colors) && colors.length > 0
    );

    const handleClick = async () => {
      // Complete onboarding step if active - step 6 (recommendations-ready) when "Show me" is clicked
      if (isStepActive('recommendations-ready')) {
        completeStep('recommendations-ready');
        console.log('🎯 OnboardingDebug: Completed recommendations-ready step via Show Me button - starting application process');
      }
      
      handleShowLoader();
      try {
        suppressOnboardingCloseSkipRef.current = true;
        setWorkflowModalOpen(false);
        setDesignerModalOpen(false);

        // Normalize keys directly using `normalizeItemKey`
        const normalizedColors = {};
        Object.keys(proposedColorsRef.current).forEach((key) => {
          const normalizedKey = normalizeItemKey(key);
          normalizedColors[normalizedKey] = proposedColorsRef.current[key];
        });

        if (!isDetectionTriggered) {
          await detectAndApplyColors(normalizedColors); // Pass normalized colors
          setIsDetectionTriggered(true); // Mark detection as triggered
        } else {
          await applyColorsToCanvasItems(normalizedColors); // Only apply colors if detection has already been done
        }

        if (isGuestUser && typeof saveUpdate === 'function') {
          try {
            console.log('[guest-thumb] Show Me completed, attempting guest snapshot save', {
              projectId,
              hasCanvasChanged,
              isDetectionTriggered,
              normalizedColorKeys: Object.keys(normalizedColors),
            });
            await waitForAnimationFrames(2);
            const didSaveGuestSnapshot = await saveUpdate();
            console.log('[guest-thumb] Show Me guest snapshot save result', {
              projectId,
              didSaveGuestSnapshot,
            });
          } catch (saveError) {
            console.error('Error creating guest design snapshot after Show Me:', saveError);
          }
        }
        
        // Complete editing-tools step when all processing is done - step 7 (editing-tools)
        // Note: This step now has a "Got it!" button, so don't auto-complete it
      } catch (error) {
        console.error("Error during button click processing:", error);
        setDesignerModalOpen(true);
        toast.error(t('assistant.detectionFailed'));
      } finally {
        handleHideLoader();
      }
    };


    return (
      <div className="designer-showme-card">
        {previewEntries.length > 0 && (
          <div className="designer-showme-preview">
            {previewEntries.map(([itemKey, colors]) => (
              <div key={itemKey} className="designer-showme-preview-item">
                <span className="designer-showme-preview-label">{formatPreviewLabel(itemKey)}</span>
                <span className="designer-showme-preview-value">{colors[0]}</span>
              </div>
            ))}
          </div>
        )}
        <Button
          variant="primary"
          className="designer-showme-button"
          onClick={handleClick}
          title={t('assistant.ShowMe')}
        >
          {isDetectionTriggered ? t('assistant.ApplyColors') : t('assistant.ShowMe')}
        </Button>
        <p className="designer-showme-note">{t('assistant.buttonWarningMessage')}</p>
      </div>
    );
  };





  const designerTrigger = !isDesignerModalOpen ? (
    designerCollapsed ? (
      <div
        className="designer-rail-collapsed"
        data-onboarding="designer-launch"
        onClick={() => {
          setDesignerCollapsed(false);
          try { localStorage.setItem('floatingDesignerCollapsed', 'false'); } catch (e) {}
        }}
        title={t('assistant.VirtualDesigner')}
      >
        <img
          src="/rnewFacadeDesignerPro_cutout80.png"
          alt="Facade Designer Icon"
          className="designer-icon"
        />
        <span>{t('assistant.VirtualDesigner')}</span>
      </div>
    ) : (
      <div
        className="designer-rail-trigger"
        data-onboarding="designer-launch"
        onClick={handleModalOpen}
      >
        <div className="designer-rail-avatar">
          <img
            src="/rnewFacadeDesignerPro_cutout80.png"
            alt="Facade Designer Icon"
            className="designer-icon"
          />
        </div>
        <span className="designer-rail-label-desktop">{t('assistant.VirtualDesigner')}</span>
        <span className="designer-rail-label-mobile">{t('assistant.Designer', { defaultValue: 'Designer' })}</span>
        <button
          type="button"
          className="designer-rail-open-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleModalOpen();
          }}
          aria-label={t('assistant.VirtualDesigner')}
          title={t('assistant.VirtualDesigner')}
        >
          <span className="designer-rail-play-icon">▶</span>
        </button>
      </div>
    )
  ) : null;

  const toggleConversationItem = (index) => {
    setExpandedHistoryIndex((prev) => (prev === index ? null : index));
  };

  const realismPrompt = t('assistant.realismPrompt', {
    defaultValue: 'Use the house photo as the base. It defines the exact boundaries, dimensions, positions, wall depth, and perspective of every opening. Fit each replacement exactly within its existing opening and align the frame naturally to the wall and trim. For entry doors, use the full designed door height from threshold to near the head jamb and the full intended width; do not shrink the door leaf or leave a large blank border around it. Use the full-facade template and close-ups for product style only. Ignore any opening positions, sizes, and alignment shown in the template; use the house photo alone to determine every real opening. Copy the exact frame color and finish for each individual window and door, along with its frame profile, sash count and arrangement, and glass design. Preserve the exact number of separate window sash units inside each outer opening; keep multiple adjacent sashes separate rather than merging them into one wide sash. Preserve window frame colors independently from the doors: do not copy dark or black door frames onto windows, and do not darken or recolor any frame. Do not transfer other door details to windows or make different units uniform. Never invent, add, remove, or alter muntins, grilles, transoms, rails, stiles, or glass divisions. Keep plain panes plain; where panes are divided, preserve the exact number, shape, placement, and proportions of divisions shown in the reference. Do not modernize, simplify, or substitute a generic style. Turn the flat drawings into realistic installed products with frame depth, installation joints, and contact shadows. For ordinary window glass, use genuinely clear, transparent, color-neutral glazing with crisp but restrained reflections of the sunny blue sky and nearby surroundings; keep the view through each pane visibly clear. Do not make clear glass frosted, milky, fogged, etched, textured, opaque, or diffusely blurred. Do not invent prominent interior objects. Preserve decorative or obscured glass only where the design reference explicitly shows it, and keep every glass division exactly. Preserve the house, camera, landscaping, and surroundings, and render the whole scene as one coherent realistic photograph.'
  });

  const handleUseRealismPrompt = () => {
    setUserInput(realismPrompt);
    setActiveDesignerMode('real');
  };

  const modeHintTitle = {
    ideas: t('assistant.ideasHintsTitle', { defaultValue: 'Ask about your home style' }),
    show: t('assistant.showHintsTitle', { defaultValue: 'Ask for a direct recommendation' }),
    real: t('assistant.realHintsTitle', { defaultValue: 'Use references to guide a realistic result' })
  }[activeDesignerMode];

  const modeHintDescription = {
    ideas: t('assistant.ideasHintsDescription', { defaultValue: 'Start with broader facade ideas, style direction, curb appeal, materials, or color themes that fit your home.' }),
    show: t('assistant.showHintsDescription', { defaultValue: 'Ask for a specific recommendation you want the designer to place automatically on the facade.' }),
    real: t('assistant.realHintsDescription', { defaultValue: 'Use your project image as the base, then add up to 8 reference images to show the exact look you want. Describe what should stay the same and what should change.' })
  }[activeDesignerMode];

  const modeHints = {
    ideas: t('assistant.ideasHints', {
      returnObjects: true,
      defaultValue: [
        'Considering the style of my house, what design direction would you recommend for the full facade?',
        'What materials, colors, and details would make my house look more current and inviting?',
        'What overall facade upgrades would give this home the best curb appeal?'
      ]
    }),
    show: t('assistant.showHints', {
      returnObjects: true,
      defaultValue: [
        'What colors would be a good choice for my windows and doors?'
      ]
    }),
    real: t('assistant.realHints', {
      returnObjects: true,
      defaultValue: [
        'Use my house photo as the base image, use my references to guide the style, and generate a more realistic facade image while keeping the same house structure.',
        'Keep the same house layout, match the attached reference style, and make windows and glass look natural and realistic.'
      ]
    })
  }[activeDesignerMode];

  const ideasModeDefaultHint = t('assistant.ideasHints', {
    returnObjects: true,
    defaultValue: [
      'Considering the style of my house, what design direction would you recommend for the full facade?',
      'What materials, colors, and details would make my house look more current and inviting?',
      'What overall facade upgrades would give this home the best curb appeal?'
    ]
  })?.[0] || '';

  const showModeDefaultHint = t('assistant.showHints', {
    returnObjects: true,
    defaultValue: [
      'What colors would be a good choice for my windows and doors?'
    ]
  })?.[0] || '';

  const modeDefaultInput = {
    ideas: ideasModeDefaultHint,
    show: showModeDefaultHint,
    real: ''
  }[activeDesignerMode] || '';

  useEffect(() => {
    setUserInput(modeDefaultInput);
  }, [activeDesignerMode, modeDefaultInput]);

  useEffect(() => {
    if (activeDesignerMode !== 'show') {
      setColorButtons([]);
      setProposedColors({});
    }
  }, [activeDesignerMode]);

  const composerHeading = {
    ideas: t('assistant.ideasComposerTitle', { defaultValue: 'Ask for design ideas' }),
    show: t('assistant.showModeComposerTitle', { defaultValue: 'Ask what to apply, then preview it' }),
    real: t('assistant.askForChanges', { defaultValue: 'Ask for design changes' })
  }[activeDesignerMode];

  const composerDescription = {
    ideas: t('assistant.ideasComposerHelp', { defaultValue: 'Ask about style, colors, materials, symmetry, curb appeal, or the best overall design direction for your house.' }),
    show: t('assistant.showModeComposerHelp', { defaultValue: 'Ask for windows, doors, colors, or recommendations, then use Show Me to place them automatically on the facade.' }),
    real: t('assistant.composerHelp', { defaultValue: 'Describe colors, materials, style direction, or ask for a new image.' })
  }[activeDesignerMode];

  const composerPlaceholder = {
    ideas: t('assistant.ideasModePlaceholder', { defaultValue: 'Ask for facade ideas, materials, colors, or overall style guidance...' }),
    show: t('assistant.showModePlaceholder', { defaultValue: 'Ask what you want changed on the house, then use Show Me...' }),
    real: t('assistant.facadeQuestionPlaceholder', { defaultValue: "What's your question or request" })
  }[activeDesignerMode];

  const modeTabs = [
    {
      id: 'ideas',
      label: t('assistant.modeIdeas', { defaultValue: 'Ideas' }),
      description: t('assistant.modeIdeasDescription', { defaultValue: 'Get facade recommendations and design guidance.' })
    },
    {
      id: 'show',
      label: t('assistant.modeShowMe', { defaultValue: 'Show Me' }),
      description: t('assistant.modeShowMeDescription', { defaultValue: 'Apply recommended windows, doors, and colors on the facade automatically.' })
    },
    {
      id: 'real',
      label: t('assistant.modeMakeItReal', { defaultValue: 'Make It Real' }),
      description: t('assistant.modeMakeItRealDescription', { defaultValue: 'Turn your canvas design into a more realistic AI-generated facade image.' })
    }
  ];

  let generatedReferenceCount = 0;
  const projectReferenceThumbnails = generatedProjectImages.map((image) => {
    const isOriginal = image?.source === 'original';
    const isSaved = image?.source === 'saved';
    const generatedIndex = image?.source === 'generated'
      ? ++generatedReferenceCount
      : 0;

    return {
      ...image,
      isOriginal,
      isSaved,
      generatedIndex,
      label: isOriginal
        ? t('assistant.originalProject', { defaultValue: 'Original project' })
        : (isSaved
          ? t('assistant.savedProjectLabel', { defaultValue: 'Saved project' })
          : t('assistant.generatedImageLabel', { defaultValue: 'Generated image {{count}}', count: generatedIndex })),
    };
  });

  const facadeAIGenerationPhaseText = facadeAIGenerationElapsedSeconds >= 25
    ? t('assistant.generatingFacadeAiPhaseLate', {
        defaultValue: 'Still working. Finalizing realism, glass, materials, and lighting.'
      })
    : t('assistant.generatingFacadeAiPhaseEarly', {
        defaultValue: 'Preparing your source image and matching the requested references.'
      });

  const makeItRealHelpIsFrench = i18n.language?.startsWith('fr');
  const makeItRealHelpThumbnail = '/site-images-videos/new-design-last-frame.png';
  const makeItRealHelpVideo = '/site-images-videos/new-design-en.mp4';
  const makeItRealHelpFrenchCaptions = '/site-images-videos/new-design-fr.vtt';
  const makeItRealHelpEnglishCaptions = '/site-images-videos/new-design-en.vtt';
  const finishResolutionChoice = (resolution) => {
    setShowResolutionModal(false);
    const resolve = resolutionChoiceRef.current;
    resolutionChoiceRef.current = null;
    if (resolve) resolve(resolution);
  };
  const renderResolutionSelector = () => {
    const usage = checkFacadeAIGenerationLimit();
    return (
      <div className="designer-resolution-selector">
        <label>
          <span>{t('assistant.imageResolutionLabel')}</span>
          <select
            className="form-select"
            value={imageResolution}
            onChange={(event) => setImageResolution(event.target.value)}
            disabled={isLoading || isFacadeAIGenerating}
          >
            {IMAGE_RESOLUTIONS.map((resolution) => (
              <option key={resolution} value={resolution}>
                {t(`assistant.imageResolution${resolution}`)}
              </option>
            ))}
          </select>
        </label>
        <p>{t('assistant.imageResolutionCreditHelp')}</p>
        <p>{t('assistant.imageResolutionTimeHelp')}</p>
        <p>{t('assistant.generationCreditsAvailable', {
          included: usage.isUnlimited ? t('assistant.unlimitedCredits') : usage.includedCreditsLeft,
          purchased: usage.extraCreditsLeft === 'Unlimited'
            ? t('assistant.unlimitedCredits') : usage.extraCreditsLeft,
        })}</p>
        {!usage.canGenerate && (
          <p className="text-danger" role="status">
            {t('assistant.insufficientGenerationCredits', { cost: usage.cost })}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      {designerTriggerContainer ? createPortal(designerTrigger, designerTriggerContainer) : designerTrigger}
      <Modal
        show={showMakeItRealNoDesignModal}
        onHide={closeMakeItRealNoDesignModal}
        centered
        size="lg"
        className="make-real-no-design-modal"
        backdropClassName="make-real-no-design-backdrop"
        aria-labelledby="make-real-no-design-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="make-real-no-design-title">
            {t('assistant.makeItRealNoDesignTitle', { defaultValue: 'Add a design before using Make It Real' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="make-real-no-design-content">
            <div className="make-real-no-design-copy">
              <p>
                {t('assistant.makeItRealNoDesignMessage', {
                  defaultValue: 'Make It Real needs an edited facade as a reference. Add windows, doors, or garage doors to your project first, then generate the realistic version.',
                })}
              </p>
              <div className="make-real-no-design-actions">
                <Button
                  variant="primary"
                  className="make-real-no-design-see-button"
                  onClick={() => setShowMakeItRealNoDesignVideo(true)}
                >
                  {t('assistant.makeItRealNoDesignSeeHow', { defaultValue: 'See how' })}
                </Button>
                <Button variant="outline-secondary" onClick={closeMakeItRealNoDesignModal}>
                  {t('assistant.makeItRealNoDesignStartDesigning', { defaultValue: 'Start designing' })}
                </Button>
              </div>
            </div>

            <button
              type="button"
              className="make-real-no-design-thumb"
              onClick={() => setShowMakeItRealNoDesignVideo(true)}
              aria-label={t('assistant.makeItRealNoDesignSeeHow', { defaultValue: 'See how' })}
            >
              <img
                src={makeItRealHelpThumbnail}
                alt={t('assistant.makeItRealNoDesignThumbnailAlt', { defaultValue: 'Video tutorial thumbnail' })}
              />
              <span className="make-real-no-design-play">▶</span>
            </button>
          </div>

          {showMakeItRealNoDesignVideo && (
            <div className="make-real-no-design-video-wrap">
              <video
                key={makeItRealHelpVideo}
                controls
                autoPlay
                playsInline
                className="make-real-no-design-video"
              >
                <source src={makeItRealHelpVideo} type="video/mp4" />
                <track
                  kind="captions"
                  src={makeItRealHelpFrenchCaptions}
                  srcLang="fr"
                  label="Francais"
                  default={makeItRealHelpIsFrench}
                />
                <track
                  kind="captions"
                  src={makeItRealHelpEnglishCaptions}
                  srcLang="en"
                  label={makeItRealHelpIsFrench ? 'Anglais' : 'English'}
                  default={!makeItRealHelpIsFrench}
                />
                {t('assistant.videoUnsupported', { defaultValue: 'Your browser does not support the video tag.' })}
              </video>
            </div>
          )}
        </Modal.Body>
      </Modal>
      <Modal
        show={showResolutionModal}
        onHide={() => finishResolutionChoice(null)}
        centered
        className="designer-resolution-modal"
        backdropClassName="designer-resolution-backdrop"
        aria-labelledby="image-resolution-modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="image-resolution-modal-title">{t('assistant.imageResolutionLabel')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderResolutionSelector()}
          {!hasEditableCanvasItems() && (
            <p className="mt-3" role="status">
              {t('assistant.makeItRealNoDesignMessage', {
                defaultValue: 'Make It Real needs an edited facade as a reference. Add windows, doors, or garage doors to your project first, then generate the realistic version.',
              })}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => finishResolutionChoice(null)}>
            {t('auth-cancel')}
          </Button>
          {hasEditableCanvasItems() ? (
            <Button
              onClick={() => finishResolutionChoice(imageResolution)}
              disabled={!checkFacadeAIGenerationLimit().canGenerate || isLoading || isFacadeAIGenerating}
            >
              {t('assistant.requestNewImage', { defaultValue: 'Request a new image' })}
            </Button>
          ) : (
            <Button onClick={() => {
              finishResolutionChoice(null);
              openMakeItRealNoDesignModal();
            }}>
              {t('assistant.makeItRealNoDesignStartDesigning', { defaultValue: 'Start designing' })}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
  <Modal show={isDesignerModalOpen} onHide={handleDesignerClose} dialogClassName="facade-designer-modal" className="facade-designer-modal" scrollable style={designerModalLayout}>
        <Modal.Header closeButton>
          <div className="designer-modal-header-content">
            <div className="designer-modal-header-copy">
              <Modal.Title>{t("assistant.VirtualDesigner")}</Modal.Title>
              <div className="designer-modal-header-subtitle">
                {t('assistant.designerWorkspaceSubtitle', {
                  defaultValue: 'Build context on the left, write and generate on the right.'
                })}
              </div>
            </div>
            <div className="designer-interaction-counts">
              <span className="designer-stat-pill">
                {`${userData.weeklyInteractionCount || 0} ${t('assistant.outOf')} ${getDesignerInteractionUsageStatus().weeklyLimit} ${t('assistant.weekly')}`}
              </span>
              <span className="designer-stat-pill">
                {`${userData.assistantInteractionCount || 0} ${t('assistant.outOf')} ${getDesignerInteractionUsageStatus().monthlyLimit} ${t('assistant.monthly')}`}
              </span>
              <span className="designer-stat-pill designer-stat-pill-accent">
                {(() => {
                  const generationLimit = getFacadeAIMonthlyLimit();
                  const generationCount = getFacadeAICurrentMonthCount();
                  const limitLabel = generationLimit === 'Unlimited' ? 'Unlimited' : generationLimit;
                  return `${generationCount} ${t('assistant.outOf')} ${limitLabel} ${t('assistant.generationCreditsMonthly')}`;
                })()}
              </span>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body ref={modalBodyRef}>
          <div className="designer-modal-layout">
            <section className="designer-panel designer-overview-panel">
              <div className="designer-overview-copy">
                <div className='designer-avatar-title'>
                  <h4>{t('assistant.overviewTitle', { defaultValue: 'Choose how you want the Virtual Designer to help' })}</h4>
                </div>
                <p>{t('assistant.overviewDescription', { defaultValue: 'Use Ideas for recommendations, Show Me to place changes directly on the facade, or Make It Real to generate a more realistic image from your design.' })}</p>
              </div>
              <div className="designer-mode-tabs" role="tablist" aria-label={t('assistant.modeSelector', { defaultValue: 'Designer modes' })}>
                {modeTabs.map((mode) => (
                  <button
                    key={mode.id}
                    type="button"
                    className={`designer-mode-tab ${activeDesignerMode === mode.id ? 'active' : ''}`}
                    onClick={() => {
                      setActiveDesignerMode(mode.id);
                      updateAppState({ aiShowModeSelected: mode.id === 'show' });
                      if (mode.id === 'show' && isStepActive('ai-interaction')) {
                        completeStep('ai-interaction');
                        console.log('🎯 OnboardingDebug: Show Me mode selected, moving to submit guidance');
                      }
                    }}
                    aria-pressed={activeDesignerMode === mode.id}
                  >
                    <span className="designer-mode-tab-title">{mode.label}</span>
                    <span className="designer-mode-tab-description">{mode.description}</span>
                  </button>
                ))}
              </div>
            </section>

            <div className="designer-workspace">
              <div className="designer-sidebar-column">
                <section className="designer-panel designer-mode-panel">
                  {(activeDesignerMode === 'ideas' || activeDesignerMode === 'show') && (
                    <>
                      <div className="designer-intro-hero designer-intro-hero-compact">
                        <div className="avatar-container designer-avatar-hero">
                          <img src={"/rnewFacadeDesignerPro_cutout300.png"} alt="Facade Designer Avatar" />
                        </div>
                        <div className="designer-section-head">
                          <h5>{modeHintTitle}</h5>
                          <p>{modeHintDescription}</p>
                        </div>
                      </div>
                      <div className="designer-hints-grid">
                        {modeHints.map((hint, index) => (
                          <div key={index} className="designer-hint-box" onClick={() => handleHintClick(hint)}>
                            {hint}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {activeDesignerMode === 'show' && (
                    <div className="designer-guided-flow">
                      <div className="designer-section-head">
                        <h5>{t('assistant.showModeTitle', { defaultValue: 'Preview recommendations on the house' })}</h5>
                        <p>{t('assistant.showModeDescription', { defaultValue: 'Ask for windows, doors, and color ideas in the chat. When a recommendation is ready, use Show Me and the designer will place the suggested items and colors on the facade automatically.' })}</p>
                      </div>
                      <div className="designer-step-list">
                        <div className="designer-step-item">
                          <span className="designer-step-number">1</span>
                          <span>{t('assistant.showModeStep1', { defaultValue: 'Ask for recommendations in the conversation below.' })}</span>
                        </div>
                        <div className="designer-step-item">
                          <span className="designer-step-number">2</span>
                          <span>{t('assistant.showModeStep2', { defaultValue: 'Review the AI answer and recommended colors/windows/doors.' })}</span>
                        </div>
                        <div className="designer-step-item">
                          <span className="designer-step-number">3</span>
                          <span>{t('assistant.showModeStep3', { defaultValue: 'Press Show Me to apply the recommendation directly on your facade.' })}</span>
                        </div>
                      </div>
                      <div className="designer-showme-actions">
                        <div className="designer-empty-state">
                          {t('assistant.showModeEmpty', { defaultValue: 'Ask for a recommendation first. When it is ready, Show Me will appear directly under the AI response in the conversation.' })}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeDesignerMode === 'real' && (
                    <div className="designer-guided-flow">
                      <div className="designer-section-head">
                        <h5>{t('assistant.realModeTitle', { defaultValue: 'Generate a more realistic facade image' })}</h5>
                        <p>{t('assistant.realModeDescription', { defaultValue: 'Use your real house image as the base, then add the canvas design as a reference so FacadeAI can create a more realistic version of your concept.' })}</p>
                      </div>
                      <div className="designer-step-list">
                        <div className="designer-step-item">
                          <span className="designer-step-number">1</span>
                          <span>{t('assistant.realModeStep1', { defaultValue: 'Keep your real facade image as the base image.' })}</span>
                        </div>
                        <div className="designer-step-item">
                          <span className="designer-step-number">2</span>
                          <span>{t('assistant.realModeStep2', { defaultValue: 'Add reference images only when you want them, using project thumbnails, uploaded images, or selected canvas items.' })}</span>
                        </div>
                        <div className="designer-step-item">
                          <span className="designer-step-number">3</span>
                          <span>{t('assistant.realModeStep3', { defaultValue: 'Use the realism prompt if helpful, then generate a new realistic version. The result will appear in the thumbnail strip as a new base option.' })}</span>
                        </div>
                      </div>
                      <div className="designer-real-visuals">
                        <div className="designer-real-visuals-title">
                          {t('homeAiGeneration.visualTitle', { defaultValue: 'From Editor Design to Accepted Final Result' })}
                        </div>
                        <div className="designer-real-visual-grid">
                          {REAL_MODE_EXAMPLE_IMAGES.map((image) => (
                            <figure className="designer-real-visual-card" key={image.src}>
                              <img
                                src={image.src}
                                alt={t(image.altKey, { defaultValue: image.altDefault })}
                                loading="lazy"
                              />
                              <figcaption className="designer-real-visual-label">
                                {t(image.labelKey, { defaultValue: image.labelDefault })}
                              </figcaption>
                            </figure>
                          ))}
                        </div>
                      </div>
                      <div className="designer-reference-setup">
                        <div className="designer-section-head">
                          <h5>{t('assistant.referenceSetupTitle', { defaultValue: 'Reference image setup' })}</h5>
                          <p>{t('assistant.referenceSetupDescription', { defaultValue: 'Add a full-facade reference from the project thumbnails below, or add a single window, door, or garage door from the canvas.' })}</p>
                          <div className="designer-reference-setup-hint">
                            <span className="designer-reference-setup-icon" aria-hidden="true">
                              <svg viewBox="0 0 24 24" focusable="false">
                                <path d="M12 2C8.686 2 6 4.686 6 8v8c0 3.314 2.686 6 6 6s6-2.686 6-6V8c0-3.314-2.686-6-6-6Zm-1 2.126V9H8V8c0-2.05 1.235-3.812 3-4.874ZM8 11h3v9c-1.654-.355-3-1.98-3-3.874V11Zm5 9.874V11h3v5c0 1.894-1.346 3.52-3 3.874ZM16 9h-3V4.126c1.765 1.062 3 2.823 3 4.874V9Z" />
                              </svg>
                            </span>
                            <span>{t('assistant.referenceSetupCanvasHint', { defaultValue: 'Select an item on the canvas, then use Use as reference image from the item controls. On desktop, the same action is also available from right-click.' })}</span>
                          </div>
                        </div>
                        {projectReferenceThumbnails.length > 0 && (
                          <div className="designer-project-reference-row">
                            <div className="designer-project-reference-label">
                              {t('assistant.projectThumbnailsLabel', { defaultValue: 'Project thumbnails' })}
                            </div>
                            <div className="designer-project-reference-grid">
                              {projectReferenceThumbnails.map((image, index) => (
                                <div className="designer-project-reference-card" key={`${image.url}-${index}`}>
                                  <button
                                    type="button"
                                    className="designer-project-reference-preview"
                                    onClick={() => setReferencePreviewImage({ src: image.url, alt: image.label })}
                                  >
                                    <img src={image.url} alt={image.label} loading="lazy" />
                                  </button>
                                  <div className="designer-project-reference-meta">
                                    <span className="designer-project-reference-name">{image.label}</span>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => addProjectThumbnailAsReference(image, image.generatedIndex || index)}
                                      disabled={isLoading || isFacadeAIGenerating}
                                    >
                                      {t('assistant.addThumbnailAsReference', { defaultValue: 'Add as reference' })}
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>

                <section className="designer-panel designer-conversation-panel">
                  <div className="designer-section-head designer-section-head-row">
                    <h5>{t('assistant.conversationTitle', { defaultValue: 'Conversation' })}</h5>
                    <span className="designer-conversation-count">
                      {conversationHistory.length > 0
                        ? t('assistant.messageCount', { defaultValue: '{{count}} messages', count: conversationHistory.length })
                        : t('assistant.readyToHelp', { defaultValue: 'Ready to help' })}
                    </span>
                  </div>
                  <div ref={responseContainerRef} className="conversation-history">
                    {(() => {
                      const reversedHistory = conversationHistory.slice().reverse();
                      const latestConversation = reversedHistory[reversedHistory.length - 1];
                      const olderConversations = reversedHistory.slice(0, -1);

                      const renderConversationContent = (conv, options = {}) => (
                        <>
                          <div className="user-message">
                            <div className="user-message-content">
                              <div className="message-text user-text">
                                <strong>{t("assistant.You")}:</strong> {conv.question}
                              </div>
                            </div>
                          </div>
                          <div className="assistant-message">
                            <div className="assistant-message-content">
                              {conv.response && !conv.response.includes('spinner-border') && !conv.response.includes('color: #1976d2') && (
                                <div className="message-text assistant-text" dangerouslySetInnerHTML={createMarkup(conv.response)} />
                              )}
                            </div>
                          </div>
                          {options.includeShowMeAction && conv.response !== null && activeDesignerMode === 'show' && !workflowModalOpen && renderButtons()}
                        </>
                      );

                      return (
                        <>
                          {olderConversations.length > 0 && (
                            <div className="conversation-history-list">
                              {olderConversations.map((conv, index) => {
                                const historyIndex = conversationHistory.length - 1 - index;
                                return (
                                <div key={index} className="conversation-history-item">
                                  <div className="conversation-history-item-header">
                                    <button
                                      type="button"
                                      className="conversation-history-toggle"
                                      onClick={() => toggleConversationItem(index)}
                                      aria-expanded={expandedHistoryIndex === index}
                                    >
                                      <span className="conversation-history-item-title">
                                        {t('assistant.previousConversation', { defaultValue: 'Previous conversation' })}
                                      </span>
                                      <span className="conversation-history-item-preview">{conv.question}</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="conversation-history-delete"
                                      onClick={() => handleDeleteConversation(historyIndex)}
                                      aria-label={t('assistant.deleteConversation', { defaultValue: 'Delete conversation' })}
                                      title={t('assistant.deleteConversation', { defaultValue: 'Delete conversation' })}
                                    >
                                      ×
                                    </button>
                                  </div>
                                  {expandedHistoryIndex === index && (
                                    <div className="conversation-history-expanded">
                                      {renderConversationContent(conv)}
                                    </div>
                                  )}
                                </div>
                              )})}
                            </div>
                          )}

                          {latestConversation && (
                            <div className="message-container latest-conversation">
                              <button
                                type="button"
                                className="conversation-history-delete latest-conversation-delete"
                                onClick={() => handleDeleteConversation(0)}
                                aria-label={t('assistant.deleteConversation', { defaultValue: 'Delete conversation' })}
                                title={t('assistant.deleteConversation', { defaultValue: 'Delete conversation' })}
                              >
                                ×
                              </button>
                              {renderConversationContent(latestConversation, { includeShowMeAction: true })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {conversationHistory.length === 0 && (
                      <div className="designer-empty-history">
                        {t('assistant.noConversationHistory', 'No conversation history yet. Start a conversation to see it here.')}
                      </div>
                    )}
                  </div>
                </section>
              </div>

              <div className="designer-main-column">
                <section className="designer-panel designer-composer-panel" ref={textareaContainerRef}>
                {activeDesignerMode !== 'real' && (
                  <div className="designer-section-head">
                    <h5>{composerHeading}</h5>
                    <p>{composerDescription}</p>
                  </div>
                )}

                {statusMessage && !isLoading && !isFacadeAIGenerating && <div className="designer-status-banner">{statusMessage}</div>}

                {activeDesignerMode === 'real' && (
                  <div className="reference-image-upload-row">
                    {renderResolutionSelector()}
                      <input
                        ref={referenceFileInputRef}
                        id="facade-reference-image-input"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleReferenceImageChange}
                        disabled={isLoading || isFacadeAIGenerating}
                        style={{ display: 'none' }}
                      />

                    <div className="designer-prompt-preview designer-realism-prompt-card">
                      <div className="designer-prompt-preview-header">
                        <div className="designer-prompt-preview-copy">
                          <div className="designer-prompt-preview-label">
                            {t('assistant.realModePromptLabel', { defaultValue: 'Recommended prompt' })}
                          </div>
                          <div className="designer-prompt-preview-text">{realismPrompt}</div>
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="designer-realism-prompt-button"
                          onClick={handleUseRealismPrompt}
                          disabled={isLoading || isFacadeAIGenerating}
                        >
                          {t('assistant.useRealismPrompt', { defaultValue: 'Use realism prompt' })}
                        </Button>
                    </div>
                    </div>

                    <div className="reference-image-upload-card">
                      <div className="reference-image-upload-header">
                        <div className="reference-image-upload-copy">
                          <label htmlFor="facade-reference-image-input" className="reference-image-upload-label">
                            {t('assistant.referenceImageLabel', { defaultValue: 'Reference images (optional, up to 8)' })}
                          </label>
                          <div className="reference-image-upload-helper">
                            {t('assistant.referenceImageHelper', { defaultValue: 'Upload inspiration photos or use the project thumbnails above to guide the final result.' })}
                          </div>
                          {activeDesignerMode === 'real' && isGuestUser && (
                            <div className="reference-image-upload-helper">
                              {hasCanvasChanged
                                ? t('assistant.makeItRealGuestAutoReference', {
                                    defaultValue: 'In Guest Mode, if you do not add a reference image manually, your current editor design will be used automatically as a temporary reference when you request a new image.',
                                  })
                                : t('assistant.makeItRealGuestNeedsEdits', {
                                    defaultValue: 'Add items or design changes in the editor first so Make It Real has a reference image to use.',
                                  })}
                            </div>
                          )}
                        </div>
                        <span className="reference-image-upload-count" aria-live="polite">
                          {t('assistant.selectedReferenceImages', {
                            defaultValue: '{{count}} reference image(s) selected',
                            count: referenceImages.length
                          })}
                        </span>
                      </div>

                      <div className="reference-image-upload-controls">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="reference-image-upload-button"
                          onClick={openReferenceImagesPicker}
                          disabled={isLoading || isFacadeAIGenerating}
                        >
                          {t('assistant.chooseReferenceImages', { defaultValue: 'Choose reference images' })}
                        </Button>
                        {referenceImages.length > 0 && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="reference-image-clear-button"
                            onClick={clearReferenceImages}
                            disabled={isLoading || isFacadeAIGenerating}
                          >
                            {t('assistant.clearReferenceImages', { defaultValue: 'Clear all' })}
                          </Button>
                        )}
                      </div>

                    {referenceImages.length > 0 && (
                      <div className="reference-image-thumbnails">
                        {referenceImages.map((image, index) => (
                          <div className="reference-image-thumb" key={image.id}>
                            <button
                              type="button"
                              className="reference-image-preview-btn"
                              onClick={() => setReferencePreviewImage({
                                src: image.dataUrl,
                                alt: image.name || `reference-${index + 1}`,
                              })}
                            >
                              <img
                                src={image.dataUrl}
                                alt={image.name || `reference-${index + 1}`}
                                loading="lazy"
                              />
                            </button>
                            <div className="reference-image-thumb-footer">
                              <button
                                type="button"
                                className="reference-image-remove-btn"
                                onClick={() => removeReferenceImage(image.id)}
                                disabled={isLoading || isFacadeAIGenerating}
                                aria-label={t('assistant.removeReferenceImageAria', {
                                  defaultValue: 'Remove reference image {{index}}',
                                  index: index + 1,
                                })}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    </div>
                  </div>
                )}

                <textarea
                  className="designer-textarea"
                  ref={textAreaRef}
                  value={userInput}
                  onChange={(e) => {
                    console.log("🔤 User input changed:", e.target.value);
                    setUserInput(e.target.value);
                  }}
                  onFocus={() => console.log("🎯 Textarea focused, current value:", userInput)}
                  onClick={() => console.log("🖱️ Textarea clicked, current value:", userInput)}
                  disabled={isLoading || isFacadeAIGenerating}
                  placeholder={composerPlaceholder}
                />


                <div className="submit-container">
                  {activeDesignerMode !== 'real' && (
                    <Button onClick={handleSubmit} disabled={isLoading || isFacadeAIGenerating}>
                      {t('assistant.Submit', { defaultValue: 'Submit' })}
                    </Button>
                  )}
                  {activeDesignerMode === 'real' && !isFacadeAIGenerating && (
                    <Button
                      variant="primary"
                      className="facade-generate-btn"
                      onClick={handleGenerateWithFacadeAI}
                      disabled={isLoading || isFacadeAIGenerating || !checkFacadeAIGenerationLimit().canGenerate}
                    >
                      {t('assistant.requestNewImage', { defaultValue: 'Request a new image' })}
                    </Button>
                  )}
                  
                  {/* Development test button - only shown in development */}
                </div>
                {errorMessage && <div className="error-message">{errorMessage}</div>}
                </section>
              </div>
            </div>
          </div>
        </Modal.Body>

      </Modal>
      <Modal
        show={workflowModalOpen}
        onHide={() => { if (!isLoading && !isFacadeAIGenerating) setWorkflowModalOpen(false); }}
        backdrop={isLoading || isFacadeAIGenerating ? "static" : true}
        keyboard={!isLoading && !isFacadeAIGenerating}
        centered
        scrollable
        container={showMeModalContainer || undefined}
        enforceFocus={false}
        restoreFocus={false}
        dialogClassName="showme-workflow-modal"
        aria-labelledby="showme-workflow-modal-title"
      >
        <Modal.Header closeButton={!isLoading && !isFacadeAIGenerating}>
          <Modal.Title id="showme-workflow-modal-title">
            {workflowMode === 'real'
              ? t('assistant.modeMakeItReal', { defaultValue: 'Make It Real' })
              : workflowMode === 'show'
                ? t('assistant.showModeProgressTitle', { defaultValue: 'Show Me recommendation' })
                : t('assistant.modeIdeas', { defaultValue: 'Ideas' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="showme-workflow-modal-body">
          {workflowMode === 'real' && isFacadeAIGenerating && (
            <div className="designer-generation-status-card" ref={generationStatusRef}>
              <div className="designer-generation-status-spinner">
                <Spinner animation="border" role="status" />
              </div>
              <div className="designer-generation-status-title">
                {t('assistant.generatingFacadeAiTitle', { defaultValue: 'Generating your realistic facade image' })}
              </div>
              <div className="designer-generation-status-text">
                {t('assistant.generatingFacadeAiMessage', { defaultValue: 'This usually takes about 30 to 60 seconds. Your new image will be applied automatically when it is ready.' })}
              </div>
              <div className="designer-generation-status-phase">
                {facadeAIGenerationPhaseText}
              </div>
              <div className="designer-generation-status-elapsed">
                {t('assistant.generatingFacadeAiElapsed', {
                  defaultValue: 'Elapsed time: {{count}}s',
                  count: facadeAIGenerationElapsedSeconds,
                })}
              </div>
              <div className="designer-generation-status-note">
                {t('assistant.generatingFacadeAiNote', { defaultValue: 'Please keep this tab open while generation finishes.' })}
              </div>
            </div>
          )}
          {isLoading && (
            <div className="showme-workflow-status" role="status" aria-live="polite">
              <Spinner animation="border" />
              <span>{statusMessage || t('assistant.thinking', { defaultValue: 'AI is thinking...' })}</span>
            </div>
          )}
          {workflowMode !== 'real' && (isLoading ? workflowProgress : conversationHistory[0]?.response) && (
            <div
              className="showme-workflow-progress"
              dangerouslySetInnerHTML={createMarkup(isLoading ? workflowProgress : conversationHistory[0].response)}
            />
          )}
          {workflowMode === 'show' && !isLoading && colorButtons.length > 0 && renderButtons()}
          {workflowMode === 'show' && !isLoading && colorButtons.length === 0 && conversationHistory[0]?.response && (
            <div className="showme-workflow-help">
              {t('assistant.showModeNoAction', { defaultValue: 'Review the response above, then try another request if you need a different recommendation.' })}
            </div>
          )}
        </Modal.Body>
      </Modal>
      <Modal
        show={Boolean(referencePreviewImage)}
        onHide={() => setReferencePreviewImage(null)}
        centered
        className="reference-image-preview-overlay"
        backdropClassName="reference-image-preview-backdrop"
        dialogClassName="reference-image-preview-modal"
      >
        <Modal.Body className="reference-image-preview-modal-body">
          {referencePreviewImage && (
            <img
              src={referencePreviewImage.src}
              alt={referencePreviewImage.alt}
              className="reference-image-preview-large"
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );


};

export default FacadeDesigner;
